// The PixiJS renderer: the pure Pixi-free seams (projection, draw-list, interpolate,
// facing, season-ground, animation-mode, present-clock, loop — each unit-tested in the
// node project without a GPU) composed into the real `Application` wiring (design D1–D10,
// UI.md §8). `CrossfadeManager`, `buildDrawables`, and `anchorDrawables` are plain logic
// unit-tested in `canvas/renderer.test.ts`; the `CanvasRenderer` class is the GPU-real
// integration (group 11) that selects directional `Sprite`s by kind/gait/facing, tints the
// wide-tier marker `Graphics` by `--colony-N`, draws the season ground behind the y-sorted
// list, and runs a `requestAnimationFrame` loop over injectable scheduler/clock seams —
// exercised manually/on-device only (D-086), its acceptance the group-12 on-device pass.

import {
  Application,
  Assets,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Texture,
  TilingSprite,
  type UnresolvedAsset,
} from 'pixi.js';
import { ActionClass, Tier } from '../net/wire/messages';
import type { WorldSnapshot } from '../net/wire/connection';
import { decodeBlobColony } from './blob-colony';
import type { BloomRingGeometry } from './bloom-ring';
import { playBoundaryHalf } from './play-boundary';
import { colonyPalette } from '../ui/match/colony-visuals';
import { beeTintFor, recolorBeePixels, type BeeTint } from './bee-palette';
import {
  BEE_BAR_HEALTH_OUTLINE_COLOR,
  BEE_BAR_HEALTH_OUTLINE_WIDTH_PX,
  healthBarColor,
  BEE_BAR_HEIGHT_PX,
  BEE_BAR_NECTAR_COLOR,
  BEE_BAR_TRACK_ALPHA,
  BEE_BAR_TRACK_COLOR,
  BEE_BAR_WIDTH_PX,
  beeBars,
  beeBarsVisibleAtScale,
} from './bee-bars';
import {
  depthScale,
  SCATTER_SIZE_MULTIPLIER,
  screenToWorld,
  spriteCameraScale,
  worldToScreen,
  Y_SQUASH,
  type Camera,
  type ScreenPoint,
} from './projection';
import {
  STATE_LABEL_CHAR_WIDTH_PX,
  STATE_LABEL_FONT_PX,
  STATE_LABEL_HALO_PAD_PX,
  StateLabelLayer,
  type StateLabelBee,
} from './state-labels';
import { paintDepth, sortDrawables, type Drawable } from './draw-list';
import { LIVE_PLAY_RENDER_INPUT, type RenderInput } from './render-input';
import { AssetRegistry, type AssetBundle, type AssetKey, type AssetManifest } from './assets';
import type { AssetLoader } from './asset-loader';
import type { PixiApp } from './pixi-app';
import { flowerSheetForPatch, groundFillColorForTile, GroundLayer } from './season-ground';
import { GROUND_GRAIN_TILE_SIZE, groundGrainTile, groundGrainTransform } from './ground-grain';
import {
  DEFAULT_SCATTER_SEED,
  scatterHalfForBoundary,
  scatterDetailForScale,
  scatterInViewport,
  type WorldRect,
} from './ground-scatter';
import { FacingDeriver, type FacingSample } from './facing';
import {
  beeSymbolRadius,
  drawsAsSymbol,
  flowerSymbolColor,
  patchSymbolRadius,
  symbolModeForScale,
} from './symbols';
import { attachedFlowerAnchor, attachmentsForFrame } from './flower-attach';
import { METER_BAR_WIDTH_PX, patchMeterDrawables, type PatchMeterRenderMode } from './patch-meter';
import { CorpseTracker, type Corpse, type CorpseFrameSample } from './corpse';
import { ARROW_LENGTH, DanceTracker, type DanceRenderMode } from './dance';
import {
  FreshCellTracker,
  HIVE_CELL_FRAME_SIZE,
  hiveCellDrawables,
  hiveCellFrame,
  type HiveCellRenderMode,
} from './hive-cells';
import { DamageFlashTracker, DEFAULT_FLASH_MS } from './damage-flash';

/** Screen-space vertices of a pointy-top hex of the given radius, y-squashed to ×0.72 so the
 * hex sits in the tilt plane (hive-egg-cells; device-only paint). */
function hexPoints(cx: number, cy: number, r: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90);
    pts.push(cx + r * Math.cos(a), cy + r * 0.72 * Math.sin(a));
  }
  return pts;
}
import { DegradationMonitor, mode, type AnimationMode, type DegradationSignal } from './animation-mode';
import { PresentationClock } from './present-clock';
import { RenderLoop } from './loop';
import {
  performanceClock,
  rafFrameScheduler,
  type Clock,
  type FrameScheduler,
} from './frame-scheduler';

/** One projected, drawable world entity — the same shape for both LOD tiers so the
 * close/wide renderers share one rendering model (client-rendering: "close tier
 * renders individual bees; wide tier renders blobs"). The `hive`/`own-queen` kinds are
 * the colony-screen anchor drawables (design D4) — derived from the render-input context,
 * not from a world record — that enter the same y-sorted list. */
export type WorldDrawableKind = 'bee' | 'blob' | 'queen' | 'patch' | 'ruin' | 'hive' | 'own-queen';

/** A close-tier bee's gait (design D1/D2): `Forage`/`Fight` fly (hover-bob + offset
 * shadow), `Build`/`Idle` walk (ground line). Selects the fly vs walk `worker` sheet. */
export type BeeGait = 'fly' | 'walk';

/** The wide-tier marker style a non-bee wide drawable renders as (design D6, the
 * `match.html`/`wide-tier.html` precedent) — replacing the old per-kind placeholder
 * circle: a colony-coloured dot `swarm` for a blob, a nested-square `hive-marker` for a
 * queen, a `bloom` ellipse for a patch, and a static `ruin-marker` for a ruin. */
export type WideMarker = 'swarm' | 'hive-marker' | 'bloom' | 'ruin-marker';

export interface WorldDrawable extends Drawable {
  kind: WorldDrawableKind;
  id: number;
  screenX: number;
  /** The sprite-sheet frame index for a close-tier bee = its client-derived heading
   * (0..11, design D3). `buildDrawables` fills it from the injected `headingFor` lookup
   * (the group-5 `FacingDeriver.heading`), falling back to the wire `facing` byte (a
   * constant 0, D-063) when no derived heading is available yet. Set only for `kind:'bee'`. */
  facing?: number;
  action?: ActionClass;
  count?: number;
  /** The gait a close-tier bee draws with (design D2): fly for `Forage`/`Fight`, walk for
   * `Build`/`Idle`. Set only for `kind:'bee'`. */
  gait?: BeeGait;
  /** The world-space point a `bee` renders attached to a flower at (flower-attach-meter,
   * finding 7), derived by `flower-attach.ts`'s `attachedFlowerAnchor` — a stable per-(bee,
   * patch) offset on the nearest gatherable patch. When set, `drawTier` draws the bee here
   * INSTEAD OF its `PresentationClock`-interpolated position (bypassing interpolation rather
   * than fighting it — see that seam's doc comment); unset for every other drawable and for a
   * bee with no qualifying patch, which renders at its own (interpolated) position as before. */
  attachedTo?: readonly [number, number];
  /** For a close-tier `bee`: the generic `worker` directional sheet the bee's `gait`
   * selects — the one body-for-all-bees (never a per-preset sheet, D1/D-090). For a
   * close-tier `patch` (design D5): its flower sheet — the deterministic live colour
   * variety when in bloom, or the dead-flower sheet past bloom. An `AssetKey`, so the
   * group-11 draw body resolves a registered texture. Unset for wide-tier drawables. */
  sheet?: AssetKey;
  /** Whether a `patch` still carries nectar (design D5): derived from the `PatchRecord.pool`
   * wire byte as `pool > 0` — exactly the predicate the former `hasNectar` bool carried —
   * driving the live-vs-dead flower `sheet` selection. Set only for `kind:'patch'`. */
  hasNectar?: boolean;
  /** The wide-tier marker style (design D6). Set for every wide-tier drawable so the draw
   * body never falls back to a placeholder circle; unset for close-tier bees (which carry
   * `gait`/`sheet` instead). */
  marker?: WideMarker;
  /** A `var(--colony-N)` colour token (design D-match-2/D-match-3), present only
   * for wide-tier `blob`/`queen` drawables and only when `buildDrawables` was given
   * a `WideColonyContext` — never set for `bee`/`patch`/`ruin` or when the context
   * is omitted (client-scaffold precedent: uncoloured, backward compatible). */
  color?: string;
  /** The region palette swap a bee body draws with (bee-body-palette): the whole body takes
   * its type colour (own) or colony colour + red band (enemy), resolved by the pure
   * `beeTintFor`. Set for every LIVE close-tier `bee` and for the `own-queen` anchor sprite;
   * unset for a corpse (which keeps the generic body under its flat grey tint), for patches,
   * for the hive tile, and for every wide-tier marker. */
  tint?: BeeTint;
  /** A live close-tier bee's health as the raw 0..255 wire fraction of ITS OWN max health —
   * `beeBars` divides by 255, never by a balance constant. Unset for a corpse. */
  health?: number;
  /** A live close-tier bee's carried nectar as the raw 0..255 wire fraction of ITS OWN
   * capacity. Unset for a corpse. */
  nectar?: number;
  /** Set only for a close-tier `bee` drawable synthesized by `corpseDrawables` (design
   * "Dead-bee corpse presentation", bee-visual-states bug c): grey-tints the sprite body
   * instead of drawing it at its natural colour. Never set for a live bee. */
  dead?: boolean;
  /** The per-entity presentation alpha of a corpse drawable (1 while held, fading to 0
   * over its fade window) — multiplied onto the tier's own crossfade alpha in `drawTier`,
   * distinct from that whole-layer alpha. Unset (defaults to 1) for every non-corpse
   * drawable. */
  corpseAlpha?: number;
  /** A live close-tier bee's damage-flash presentation intensity, 0..1 (design
   * "The damage flash is an outline-primary treatment on its own per-drawable field" /
   * design E7/E8), read from `DamageFlashTracker.flashes` — modelled on `corpseAlpha`, its
   * OWN field, **never** folded into `tint`: a `bee-palette.ts` variant is a whole-sheet
   * texture cached by resolved colour, and a transient per-bee flash colour would blow that
   * cache open (design E8). Composes OVER the live `tint` at draw time — the palette-swapped
   * texture selection is completely unaffected; the flash paints as a separate outline +
   * reduced-alpha body-tint companion (`flashPaintFor`). Unset for every non-bee drawable,
   * for a live bee with no active flash, and — structurally, never explicitly checked here —
   * for a `dead` corpse: `corpseDrawables` never accepts a flash lookup at all, so a corpse
   * cannot carry a stray value, and `flashPaintFor` refuses to paint one regardless. */
  flashIntensity?: number;
  /** Whether this drawable is the selected entity, drawn with a blue outline silhouette. Set only
   * for close-tier bees; absent (never `false`) for everything else, matching `flashIntensity`'s
   * "unset means no" convention. */
  selected?: boolean;
}

/** Maps a bee's action class to its gait sheet (design D2): `Forage`/`Fight` fly (hover-bob
 * + offset shadow), `Build`/`Idle` walk (ground line). Pure and wire-independent — the
 * `Idle`-mid-field edge reads as walking (in-hive / stationary), an accepted trade. */
export function gaitForAction(action: ActionClass): BeeGait {
  return action === ActionClass.Forage || action === ActionClass.Fight ? 'fly' : 'walk';
}

/** The per-bee ground depth cue the draw body paints beneath a close-tier bee (design
 * D2/D7, `close-bee-tiers.html`): a flying bee in `full` mode hovers above an **offset
 * filled shadow ellipse**; a flying bee in `reduced`/`none` mode drops the bob **and** the
 * shadow (`none`); a **walking** bee sits on the **same filled ellipse, un-offset**, with no
 * bob, in every mode.
 *
 * **The walking treatment was a stroked ground LINE until 2026-08-11**, and that was a
 * deliberate decision matching the kit mock's `groundLine` — reverted on a presenter finding
 * that a flat line does not read as a shadow beside the flying bees' ellipses (the earlier
 * revert this file used to record, restored). One ellipse for both gaits also collapses three
 * treatments into two: what now distinguishes flying from walking is the hover gap and the
 * ellipse's x-offset, not a different shape.
 *
 * Pure over `(gait, mode)`, unit-tested so the treatments are asserted separately from the
 * GPU-real `drawTier` body (D-086). */
export type DepthCue = 'hover-shadow' | 'ground-shadow' | 'none';

export function depthCueFor(gait: BeeGait, animMode: AnimationMode): DepthCue {
  if (gait === 'walk') return 'ground-shadow';
  return animMode === 'full' ? 'hover-shadow' : 'none';
}

/** The per-frame degradation input the production RAF loop feeds the `DegradationMonitor`
 * (design D7): the streamed `ClosePayload.truncated`/`totalCount` cap pressure, plumbed
 * onto the `WorldSnapshot` from the close keyframe (never a hard-coded `false`) so the
 * truncation arm of the degradation trigger is live in production, not only in the seam
 * test. A wide frame (or no world yet) reports no truncation. Pure over the snapshot, so
 * the "the production path reads the wire truncation bit" wiring is unit-testable without a
 * GPU (the `DegradationMonitor.sample` → `mode` arm is already covered). */
export function degradationSignalFor(world: WorldSnapshot | null): DegradationSignal {
  return {
    truncated: world?.truncated ?? false,
    totalCount: world?.totalCount ?? 0,
  };
}

/**
 * Resolves the degradation monitor a `CanvasRenderer` uses (design **D-E**, client-rendering
 * "the renderer's degradation monitor is injectable so a caller can pin the animation mode"):
 * the caller's `injected` monitor when supplied, else exactly the frame-guarded monitor the
 * renderer has always constructed — the same threshold (`DEGRADE_BELOW_FPS`) and the same
 * anti-flap window (`DegradationMonitor`'s own `DEFAULT_DEGRADATION_WINDOW_MS` default, left
 * unset here exactly as it always has been). This is additive and changes no shipped
 * behaviour: the constructor calls this with nothing else in between, so "no monitor
 * injected" and "today's construction" are the same code path, not two that could drift.
 *
 * A benchmark harness injects a monitor built with no `frameGuard` (and never reports
 * `truncated`), which is a monitor that structurally cannot degrade — not one that merely
 * happens not to, so a workload cannot get cheaper because the device it is measuring is
 * slow (design D-E's whole point).
 *
 * Pure and exported so the injection contract is testable without a GPU (D-086): nothing
 * here touches Pixi.
 */
export function resolveDegradationMonitor(
  clock: Clock,
  injected?: DegradationMonitor,
): DegradationMonitor {
  return (
    injected ??
    new DegradationMonitor(clock, {
      frameGuard: { minFps: DEGRADE_BELOW_FPS },
    })
  );
}

/** What `buildDrawables` needs to decode + colour wide-tier blobs and queens by
 * colony (design D-match-3): the match's `fieldSize` (for `unpackBlobId`) and the
 * roster (`manifest.colonyIds`) a decoded blob is validated against. Omitting this
 * entirely (e.g. before `manifest` has arrived) renders the wide tier uncoloured —
 * never a crash, never a guessed colour. */
export interface WideColonyContext {
  fieldSize: number;
  colonyIds: readonly number[];
  /** The manifest's `playMarginU` — the apron beyond the field edge bees may move into
   * (`deathmatch-world` D-6). Present for the world extent, not for wide-tier colouring: it
   * fixes where the perimeter is drawn and how far ground scatter sprays. Omitted (or 0) is a
   * world with no apron, whose boundary is the field edge. */
  playMarginU?: number;
}

/** Projects every entity in `world` (whichever tier it carries) to one y-sorted list
 * of drawables — the single rendering model both LOD tiers share (D10). Pure.
 * `wideColonyContext`, when supplied, colours wide-tier blobs (decoded via
 * `unpackBlobId`, skipping — never aliasing — an out-of-roster colony) and queens
 * (by their own `colonyId` field) with the shared `--colony-*` palette.
 *
 * `headingFor` (close-tier only) resolves a bee's client-derived heading (0..11) by id —
 * the group-5 `FacingDeriver.heading`, wired in by the group-11 draw body. Each bee's frame
 * is that derived heading, falling back to the wire `facing` byte (a constant 0, D-063) when
 * the lookup is omitted or has no heading for that bee yet, so the pre-change call sites keep
 * working (the derivation state lives in the renderer, never in this pure function).
 *
 * `ownColonyId` (close-tier only, bee-body-palette) is the viewer's own colony: it selects
 * each bee's body/band palette swap via `beeTintFor` — own bees by type colour, enemies by
 * colony colour with a red band. Omitted or `null` (a spectator, or before the `Welcome`
 * lands) renders every colony neutral rather than marking every colony an enemy.
 *
 * `attachedTo` (close-tier only, flower-attach-meter review finding), when supplied, resolves
 * a bee's flower anchor by id from a map the caller precomputed ONCE per accepted close frame
 * (`flower-attach.ts`'s `attachmentsForFrame`) — the production `CanvasRenderer` wires this so
 * the O(bees × patches) attachment scan runs at the streamed close-tier cadence rather than
 * every RAF frame. Omitted (every pure-function test that predates this parameter, and the
 * default for any other call site), this recomputes the SAME result inline per bee via
 * `attachedFlowerAnchor`, exactly as before — so this parameter is additive and every
 * pre-change call site keeps its exact existing behaviour.
 *
 * `flashFor` (close-tier only, combat-strikes-and-damage-flash), when supplied, resolves a
 * bee's current damage-flash intensity (0..1) by id — `DamageFlashTracker.flashes`, wired in
 * by the group-11 draw body the same once-per-RAF-frame way `headingFor`/`attachedTo` are.
 * Omitted (every pre-change call site), no bee carries `flashIntensity` — never a crash,
 * never a guessed flash. */
export function buildDrawables(
  camera: Camera,
  world: WorldSnapshot,
  wideColonyContext?: WideColonyContext,
  headingFor?: (beeId: number) => number | undefined,
  ownColonyId?: number | null,
  attachedTo?: (beeId: number) => readonly [number, number] | undefined,
  flashFor?: (beeId: number) => number | undefined,
  selectedBeeId?: number | null,
): WorldDrawable[] {
  const drawables: WorldDrawable[] = [];

  if (world.tier === Tier.Close) {
    for (const bee of world.bees.values()) {
      const screen = worldToScreen(camera, bee.position);
      const gait = gaitForAction(bee.action);
      drawables.push({
        kind: 'bee',
        id: bee.id,
        // Unset rather than `false` for an unselected bee, matching `flashIntensity`'s convention.
        ...(selectedBeeId != null && bee.id === selectedBeeId ? { selected: true } : {}),
        screenX: screen.x,
        screenY: screen.y,
        // Derived heading (D3) if the lookup has one, else the wire `facing` byte (0, D-063).
        facing: headingFor?.(bee.id) ?? bee.facing,
        action: bee.action,
        gait,
        sheet: gait === 'fly' ? 'workerFly' : 'workerWalk',
        // One generic body, recoloured per (type | colony) — the colour carries the identity,
        // not the sheet (bee-body-palette, closing D-090).
        tint: beeTintFor(bee.colonyId, bee.typeId, ownColonyId ?? null),
        // Raw wire bytes; `beeBars` owns the /255 conversion and the suppression rules.
        health: bee.health,
        nectar: bee.nectar,
        // The damage flash's own field (combat-strikes-and-damage-flash design E7/E8):
        // `undefined` when no lookup is supplied or it has nothing active for this bee —
        // never defaulted to 0, so "no entry" and "an expired-to-zero flash" stay distinct
        // from a caller's point of view (mirrors `attachedTo`'s own `undefined` contract).
        flashIntensity: flashFor?.(bee.id),
      });
    }
  } else {
    for (const blob of world.blobs.values()) {
      let color: string | undefined;
      if (wideColonyContext) {
        const colonyId = decodeBlobColony(
          blob.blobId,
          wideColonyContext.fieldSize,
          wideColonyContext.colonyIds,
        );
        if (colonyId === null) {
          continue; // out-of-range decode: skip, never alias another colony's blob
        }
        color = colonyPalette(colonyId);
      }
      const screen = worldToScreen(camera, blob.centroid);
      drawables.push({
        kind: 'blob',
        id: blob.blobId,
        screenX: screen.x,
        screenY: screen.y,
        action: blob.dominantAction,
        count: blob.count,
        marker: 'swarm',
        color,
      });
    }
    for (const queen of world.queens.values()) {
      const screen = worldToScreen(camera, queen.position);
      drawables.push({
        kind: 'queen',
        id: queen.id,
        screenX: screen.x,
        screenY: screen.y,
        marker: 'hive-marker',
        color: wideColonyContext ? colonyPalette(queen.colonyId) : undefined,
      });
    }
    for (const ruin of world.ruins.values()) {
      const screen = worldToScreen(camera, ruin.position);
      drawables.push({
        kind: 'ruin',
        id: ruin.id,
        screenX: screen.x,
        screenY: screen.y,
        marker: 'ruin-marker',
      });
    }
  }

  // Patches appear in both tiers' payloads (keyframe_delta.rs). The wide tier draws a
  // `bloom` marker ellipse (D6); the close tier draws the flower dressing (design D5): a
  // live colour-variety sheet in bloom, the dead-flower sheet past bloom. `hasNectar` is
  // copied straight through onto every patch drawable (the one-field client gap this pass
  // closes), driving that live-vs-dead selection.
  for (const patch of world.patches.values()) {
    const screen = worldToScreen(camera, patch.position);
    drawables.push({
      kind: 'patch',
      id: patch.id,
      screenX: screen.x,
      screenY: screen.y,
      hasNectar: patch.pool > 0,
      marker: world.tier === Tier.Wide ? 'bloom' : undefined,
      sheet: world.tier === Tier.Wide ? undefined : flowerSheetForPatch(patch),
    });
  }

  // Flower attachment (flower-attach-meter, finding 7): mark each close-tier `bee` drawable
  // gathering from a patch as attached to it (a stable per-(bee, patch) offset, `flower-
  // attach.ts`), so `drawTier`'s existing per-bee position resolution can render it snapped
  // to the flower instead of at its own (possibly just-arrived-at) position. A separate pass
  // over the already-built list — rather than folded into either loop above — so it needs no
  // edit to the bee or patch drawable construction themselves.
  if (world.tier === Tier.Close) {
    for (const drawable of drawables) {
      if (drawable.kind !== 'bee') continue;
      if (attachedTo !== undefined) {
        drawable.attachedTo = attachedTo(drawable.id);
        continue;
      }
      const bee = world.bees.get(drawable.id);
      if (bee === undefined) continue;
      drawable.attachedTo = attachedFlowerAnchor(bee, world.patches.values());
    }
  }

  return sortDrawables(drawables);
}

/** The fixed drawable id shared by the two colony-screen anchor drawables (design D4).
 * There is exactly one own hive and one own queen, so a constant id — disambiguated by
 * `kind` in the draw body's per-entity sprite-map key (`${kind}:${id}`) — keys them
 * stably across frames. */
const ANCHOR_ID = 0;

/**
 * The synthetic facing-deriver id for the own-queen anchor.
 *
 * Negative deliberately: wire bee ids are unsigned, so no real bee can ever collide with it, and a
 * collision would mean the queen's heading and some bee's heading overwrote each other every frame.
 * `ANCHOR_ID`'s `0` is a *drawable* id (namespaced by `kind`) and is unsafe to reuse here, because
 * the facing deriver's map is keyed by id alone with no kind.
 */
export const QUEEN_FACING_ID = -1;

/**
 * The own-hive tile's rendered height (screen px) in the colony establishing shot, used as the
 * hive drawable's painter's-algorithm depth bias (hive-depth-order). The hive sprite is
 * BOTTOM-anchored (`anchor 0.5,1` in `createNode`) at the projected hive centre and drawn from
 * the 120×106 `hive_tilted` source, so its visible tile spans its FULL rendered height ABOVE
 * that anchor line. Biasing the hive's depth key UP by this height puts its sort key at the
 * tile's TOP edge, so ground-level bees clustered at the comb (feet below the top edge) sort
 * after the tile and paint OVER it, while the own-queen (at the anchor) and bees clearly behind
 * the hive (feet above the top edge) keep the correct order.
 *
 * = 106 (source height) × 1.05 (the depth scale of a centred anchor: `depthScale` is
 * `0.85 + 0.4·(y/h)`, and a centred hive has `y/h ≈ 0.5` → 1.05, independent of the actual
 * viewport size, so this is a robust display constant for the fixed establishing shot). The
 * full height — not half — is deliberate: the bottom-anchored tile's top edge is a full height
 * above the anchor, and a slight overshoot only affects bees ABOVE the tile (which do not
 * overlap it), whereas an undershoot would re-hide upper-comb bees. Real pan/zoom is deferred
 * (D-071), so the hive stays centred and this constant holds.
 */
export const HIVE_DEPTH_KEY_RISE = 111;

/**
 * One hive cell's drawn "radius" in WORLD units (the hex is then camera-scaled and
 * tilt-squashed like everything else).
 *
 * Deliberately larger than half the `hive.cell_spacing_u` (4 u) lattice pitch: at tangent
 * size the comb rendered as a field of separate dots, when what it is supposed to read as
 * is one continuous mass whose silhouette IS the colony's build state (UI.md §8). At this
 * radius neighbours overlap and the stamped union closes up, which is also what makes a
 * growing comb legible as growth rather than as more speckles.
 */
export const HIVE_CELL_WORLD_RADIUS = 6;

/** A shared empty drawable list, so the not-in-symbol-mode path allocates nothing per frame. */
const EMPTY_DRAWABLES: readonly WorldDrawable[] = [];

/** The own-queen anchor sprite's palette swap (bee-body-palette). She is always the viewer's
 * own, always typeless, so her tint is a constant — resolved once rather than per frame. */
const OWN_QUEEN_TINT = beeTintFor(0, null, 0);

/**
 * Derives the colony-screen own-hive + own-queen anchor drawables from the render-input
 * context (design D4): a static `hive` tile and a fixed-frame `own-queen` sprite at the
 * `Welcome.yourHive` world anchor, projected through the same tilt/squash as every other
 * entity and returned as `WorldDrawable`s so they enter the one shared y-sorted list. Pure.
 *
 * The hive tile enters the list with an explicit painter's-algorithm `depth` at its rendered
 * TOP edge (`screen.y − HIVE_DEPTH_KEY_RISE`), decoupled from its unchanged `screenX`/`screenY`
 * draw position, so ground-level bees clustered at the comb paint OVER the tile while the queen
 * (keyed on the anchor `screen.y`, strictly greater than the hive's top-edge key) still paints
 * ATOP it and bees clearly behind the hive (feet above its top edge) paint UNDER it
 * (hive-depth-order).
 *
 * Returns nothing (an empty list, never a throw) when the own colony has no anchor
 * (`ownHiveAnchor === null` — spectator / pre-handshake) or has been eliminated
 * (`ownColonyLifecycle !== 'live'` — the colony dies with the queen and its comb becomes
 * neutral ruins, streamed separately, DESIGN §2), so the whole anchor clears. The queen's
 * liveness comes from the own-colony lifecycle, never from a streamed close-tier queen (the
 * wire carries none for the own colony, D-091). The caller merges this only on the colony
 * screen (task 8.2 / the group-11 draw body reading `lastRenderInput`).
 */
export function anchorDrawables(
  camera: Camera,
  renderInput: RenderInput,
  queenHeading?: number,
): WorldDrawable[] {
  if (renderInput.ownHiveAnchor === null || renderInput.ownColonyLifecycle !== 'live') {
    return [];
  }
  const screen = worldToScreen(camera, renderInput.ownHiveAnchor);
  const anchor: WorldDrawable[] = [
    {
      kind: 'hive',
      id: ANCHOR_ID,
      screenX: screen.x,
      screenY: screen.y,
      // Sort at the tile's top edge, not its centre anchor, so comb bees paint over it.
      depth: screen.y - HIVE_DEPTH_KEY_RISE,
      sheet: 'hive',
    },
    {
      kind: 'own-queen',
      id: ANCHOR_ID,
      screenX: screen.x,
      screenY: screen.y,
      sheet: 'ownQueen',
      // The queen is a sixth, typeless entity (DESIGN §3) with her own `--bee-queen` token —
      // the same palette swap every own bee gets, so she reads as the colony's queen rather
      // than as another amber worker (bee-body-palette).
      tint: OWN_QUEEN_TINT,
      // **Her heading, where the caller can derive one.** She was a fixed frame-0 sprite (design
      // D4) — which points her permanently up — and that was harmless while the anchor was the
      // static hive point. It stopped being harmless once the demo pinned the anchor to her own
      // simulated position: she then visibly walks around the comb while always facing north.
      // Absent (`undefined`) keeps the old fixed-frame behaviour for any caller with no heading
      // source, so live mode's static hive anchor is unchanged.
      facing: queenHeading,
    },
  ];
  return sortDrawables(anchor);
}

/**
 * Projects every live-or-fading tracked corpse (`CorpseTracker.corpses`) into a close-tier
 * `bee` drawable (design "Dead-bee corpse presentation", bee-visual-states bug c): grounded
 * (`gait: 'walk'`, so the existing walk depth cue draws no hover-bob and no shadow), the
 * frozen heading as `facing`, the generic walking sheet, `dead: true` (the GPU-real draw
 * body grey-tints on this), and the corpse's current fade `corpseAlpha`. Pure — the same
 * `kind:'bee', id` pairing as the bee's live drawable, so `drawTier`'s create/mutate/destroy
 * diff carries the SAME Pixi node across the live→corpse transition (no flicker) and
 * destroys it only once the corpse itself expires and stops appearing here.
 */
/** Maps a wire `tagHash` to a stable cosmetic arrow hue (dance-visuals): same-tag dances
 * share a colour, different tags read apart — never colony/bee identity. A compact
 * HSL(hue, 62%, 55%) → 0xRRGGBB with hue = `tagHash % 360`. */
export function danceTagColor(tagHash: number): number {
  const h = ((tagHash % 360) + 360) % 360;
  const s = 0.62;
  const l = 0.55;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (v: number): number => Math.round((v + m) * 255) & 0xff;
  return (to(r) << 16) | (to(g) << 8) | to(b);
}

/**
 * Substitutes each bee's `PresentationClock`-interpolated world position for its raw
 * confirmed tick position, falling back to the raw position when the clock has no sample
 * for that id (mirrors `drawTier`'s own bee-interpolation fallback, design D8) — so the
 * `DanceWorld.bees` fed into the pure, position-agnostic `canvas/dance.ts` seam anchors
 * the dance arrow/indicator/watcher pips at the SAME interpolated body the bee sprite
 * itself draws at, not the confirmed `WorldSnapshot` tick position (dance-visuals; D-132:
 * the arrow/glyph previously lagged the sprite by up to one ~10 Hz interpolation step).
 * Pure and exported for unit testing — `dance.ts` itself stays unmodified and
 * interpolation-agnostic; this is the only place interpolation is threaded in.
 */
export function interpolatedDanceBees(
  bees: ReadonlyMap<number, { readonly position: readonly [number, number] }>,
  positionAt: (id: number) => readonly [number, number] | undefined,
): ReadonlyMap<number, { readonly position: readonly [number, number] }> {
  const out = new Map<number, { readonly position: readonly [number, number] }>();
  for (const [id, bee] of bees) {
    out.set(id, { position: positionAt(id) ?? bee.position });
  }
  return out;
}

export function corpseDrawables(camera: Camera, corpses: readonly Corpse[]): WorldDrawable[] {
  return corpses.map((corpse) => {
    const screen = worldToScreen(camera, corpse.position);
    return {
      kind: 'bee',
      id: corpse.id,
      screenX: screen.x,
      screenY: screen.y,
      facing: corpse.heading,
      gait: 'walk',
      sheet: 'workerWalk',
      dead: true,
      corpseAlpha: corpse.alpha,
    };
  });
}

/**
 * The full y-sorted drawable list for one rendered tier layer (design D4, hive-egg-cells,
 * colony-match-nav): `buildDrawables`' tier-native entities, plus — **only on the Close
 * tier** — the colony-screen own-hive/own-queen anchor (suppressed once live per-cell hive
 * state has arrived, hive-egg-cells) and any live-or-fading corpses. Both the anchor and the
 * corpse tracker are close-tier-only concepts ("Dead-bee corpse presentation" bug c): a
 * Wide-tier snapshot — e.g. the Colony screen zoomed out past the LOD hysteresis to the blob
 * subscription (colony-match-nav removed the zoom-driven Colony↔Match navigation, so this
 * combination is newly reachable in production) — never merges either, so its own
 * blob/queen/patch/ruin drawables render standalone rather than assuming an own-hive anchor
 * is present. Pure and exported so this merge decision is unit-tested independent of the
 * GPU-real `onFrame` body it feeds (D-086).
 */
export function tierDrawables(
  camera: Camera,
  world: WorldSnapshot,
  wideColonyContext: WideColonyContext | undefined,
  headingFor: ((beeId: number) => number | undefined) | undefined,
  renderInput: RenderInput,
  corpses: readonly Corpse[],
  attachedTo?: (beeId: number) => readonly [number, number] | undefined,
  flashFor?: (beeId: number) => number | undefined,
): WorldDrawable[] {
  const base = buildDrawables(
    camera,
    world,
    wideColonyContext,
    headingFor,
    renderInput.ownColonyId,
    attachedTo,
    flashFor,
    renderInput.selectedBeeId,
  );
  if (world.tier !== Tier.Close) {
    return base;
  }
  const queenHeading = headingFor?.(QUEEN_FACING_ID);
  const anchors =
    world.cells.size > 0
      ? anchorDrawables(camera, renderInput, queenHeading).filter((d) => d.kind !== 'hive')
      : anchorDrawables(camera, renderInput, queenHeading);
  return sortDrawables([...base, ...anchors, ...corpseDrawables(camera, corpses)]);
}

/**
 * The per-node placement `drawTier` applies to a drawable's Pixi container each frame
 * (hive-depth-order): the rounded draw `y` at the node's LIVE screen-y (the interpolated
 * position for a moving bee, else the projection) and the per-node `zIndex` — authoritative
 * for paint because the entity layer is `sortableChildren` — from the SAME shared
 * {@link paintDepth} rule the pure list sort uses. A keyed tall tile (the hive) keys on its
 * explicit `depth` regardless of the live screen-y; a keyless drawable keys on the live
 * screen-y, so its paint order tracks its rendered position rather than a stale snapshot. Pure
 * and unit-tested; `drawTier`'s one-line assignment of these values to the Pixi node is the
 * GPU-real boundary (D-086).
 */
export function resolveNodePlacement(
  drawable: WorldDrawable,
  liveScreenY: number,
): { y: number; zIndex: number } {
  return { y: Math.round(liveScreenY), zIndex: paintDepth(drawable, liveScreenY) };
}

/**
 * The single screen-position resolution rule a close-tier bee drawable's body ({@link
 * CanvasRenderer.drawTier}) and its health/nectar bars ({@link CanvasRenderer.drawBeeBars})
 * MUST share (bee-body-palette finding: the bars read only the interpolated position and
 * ignored the drawable's flower `attachedTo` anchor, so an attached bee's bars visibly
 * detached from its snapped-to-the-flower body). A flower-attached bee (flower-attach-meter)
 * draws at that fixed world anchor INSTEAD of the `PresentationClock`-interpolated
 * position — bypassing interpolation rather than fighting it frame to frame, since the
 * anchor is a fixed point, not a fresh sample to lerp toward. Every other drawable kind (and
 * a bee with neither an anchor nor a tracked position) keeps its raw projected position.
 * Pure given the same inputs; `positionAt` and `now` are the only impure-looking
 * parameters, injected so this stays testable without a live `PresentationClock`.
 */
export function beeDrawScreenPosition(
  drawable: WorldDrawable,
  camera: Camera,
  positionAt: (id: number, now: number) => readonly [number, number] | undefined,
  now: number,
): ScreenPoint {
  if (drawable.kind === 'bee') {
    const worldPos = drawable.attachedTo ?? positionAt(drawable.id, now);
    if (worldPos !== undefined) {
      return worldToScreen(camera, worldPos);
    }
  }
  return { x: drawable.screenX, y: drawable.screenY };
}

/** The bee-bar overlay's stage `zIndex`. A named constant only so the state-label layer's
 * place ABOVE it is assertable rather than a pair of literals that could drift apart. */
export const BEE_BAR_OVERLAY_Z = 999;

/** The state-label layer's stage `zIndex` — its OWN container, above the bar overlay so labels
 * are never interleaved with entities or bars, and below the bloom ring (1000) and dance cues
 * (1001). The bars sit under those two on the principle that a per-bee readout must not occlude
 * a match-level cue; a state label is the same kind of readout, so it inherits the same ceiling
 * rather than claiming the top of the stage. */
export const STATE_LABEL_LAYER_Z = 999.5;

/** The state label's ink (`--ink-1`) and paper halo (`--paper-0` at 0.92), design **E12**:
 * bare ink is legible over all three season fills but fails over the honey-coloured comb and
 * where a label crosses a bee's dark stripes; the halo fixes exactly those for one extra
 * stroke and adds no rectangle to the field. */
const STATE_LABEL_INK = '#2B2114';
const STATE_LABEL_HALO = 'rgba(253,249,239,0.92)';

/** The cached texture is rasterised at this multiple of {@link STATE_LABEL_FONT_PX} and drawn
 * back down by the same factor. The label's on-screen size is `10 × (zoom / 2) × depth`, which
 * is ~15 px at the shipped 3.08 opening framing — upscaling a 10 px raster to that would be a
 * blur, so the raster is authored above it. (The pass-1 sketch renders its canvas at the same
 * 2× for the same reason.) */
const STATE_LABEL_TEXTURE_SUPERSAMPLE = 2;

/**
 * The label layer's per-bee input for one frame: every close-tier bee drawable the renderer is
 * about to draw, with its **drawn** screen position (from the one shared
 * {@link beeDrawScreenPosition} rule, so a flower-attached bee's label tracks its snapped body
 * exactly as its bars had to be corrected to) and its resolved B++ state name.
 *
 * `beeStateNames` is the rounds-only `RenderInput` channel (design **E5**). **Absent** means the
 * live path — no trace, no state names, and so no label bees at all rather than a list of
 * sentinels; a bee merely missing from a present map is the trace's no-state sentinel (a bot
 * colony's bee, or a queen) and carries `null`, which the layer never emits for.
 *
 * Pure — the impure-looking `positionAt`/`now` are injected exactly as they are for
 * `beeDrawScreenPosition`, so this stays testable without a clock or a GPU.
 */
export function stateLabelBees(
  camera: Camera,
  drawables: readonly WorldDrawable[],
  beeStateNames: ReadonlyMap<number, string> | undefined,
  colonyOf: (id: number) => number | undefined,
  positionAt: (id: number, now: number) => readonly [number, number] | undefined,
  now: number,
): StateLabelBee[] {
  if (beeStateNames === undefined) {
    return [];
  }
  const bees: StateLabelBee[] = [];
  for (const drawable of drawables) {
    // A corpse carries no state — it is not in one — so it is not a label bee either.
    if (drawable.kind !== 'bee' || drawable.dead === true) {
      continue;
    }
    const { x, y } = beeDrawScreenPosition(drawable, camera, positionAt, now);
    bees.push({
      id: drawable.id,
      colonyId: colonyOf(drawable.id) ?? -1,
      stateName: beeStateNames.get(drawable.id) ?? null,
      screenX: x,
      screenY: y,
    });
  }
  return bees;
}

/**
 * The renderer's state-label bookkeeping: the pure {@link StateLabelLayer} and the
 * **per-distinct-string** texture cache, held together because they are invalidated together
 * (design **E8**) — on renderer destruction, on trace load and on version switch, where neither
 * the bee ids, the baselines nor the state names carry over.
 *
 * Generic over the texture type and constructed with an injected factory (the `RenderLoop`
 * precedent), so the cache's contract — one texture per distinct string, populated **lazily**,
 * released on invalidation — is asserted in the node project while the real
 * `CanvasRenderer` builds Pixi `Texture`s with the same class.
 *
 * Lazy population is what bounds the resident set without an eviction policy: the format admits
 * 65 535 names and a rounds roster ~1 008, but a texture exists only for a name actually
 * **drawn**, so the bound is what the suppression gate and the zoom floor already admit on
 * screen. A name no bee is ever in costs nothing.
 */
export class StateLabelPainter<T> {
  /** The pure layer deciding which labels are live, where they sit and who survives a crowd. */
  readonly labels = new StateLabelLayer();
  private readonly textures = new Map<string, T>();

  constructor(
    private readonly create: (name: string) => T,
    private readonly release: (texture: T) => void,
  ) {}

  /** The cached texture for `name`, built on first **draw** and never before. */
  texture(name: string): T {
    const cached = this.textures.get(name);
    if (cached !== undefined) {
      return cached;
    }
    const built = this.create(name);
    this.textures.set(name, built);
    return built;
  }

  get textureCount(): number {
    return this.textures.size;
  }

  /** Releases every texture and drops the layer's per-run state. Without this half the key
   * space is per-run and a long sandbox session iterating a dozen Runs accumulates a texture
   * for every state name it has ever rendered. */
  invalidate(): void {
    for (const texture of this.textures.values()) {
      this.release(texture);
    }
    this.textures.clear();
    this.labels.reset();
  }
}

/** The playback-side signals the label layer needs and the world frame does not carry: the
 * review subject colony, whether the playhead is being dragged, a one-frame seek pulse, and
 * the trace's own answer to "does this bee appear later?" (design **E9** — a pick clears on
 * the bee's fate in the TRACE, never on its absence from the frame on screen). Set by the
 * rounds screens; the live path never touches it. */
export interface StateLabelPlayback {
  readonly subjectColonyId: number | null;
  readonly scrubbing?: boolean;
  /** Consumed by the next drawn frame and then cleared — a seek is an event, not a mode. */
  readonly seek?: boolean;
  readonly pickedBeeAppearsLater?: (beeId: number) => boolean;
}

/**
 * Retains the old render snapshot alongside the new one across a crossfade (D9),
 * discarding the old only once `advance` has accumulated the full motion budget.
 * `swap(next, false)` (a viewport-only re-key) replaces `current` with no crossfade
 * and does not disturb any in-flight `old`. Pure, generic over the snapshot type.
 */
export class CrossfadeManager<T> {
  private currentValue: T;
  private oldValue: T | null = null;
  private elapsedMs = 0;

  constructor(initial: T, private readonly motionBudgetMs: number) {
    this.currentValue = initial;
  }

  get current(): T {
    return this.currentValue;
  }

  get old(): T | null {
    return this.oldValue;
  }

  get progress(): number {
    return Math.min(1, this.elapsedMs / this.motionBudgetMs);
  }

  isCrossfading(): boolean {
    return this.oldValue !== null;
  }

  swap(next: T, crossfade: boolean): void {
    if (crossfade) {
      this.oldValue = this.currentValue;
      this.elapsedMs = 0;
    }
    this.currentValue = next;
  }

  advance(elapsedMs: number): void {
    if (this.oldValue === null) {
      return; // nothing in flight
    }
    this.elapsedMs += elapsedMs;
    if (this.elapsedMs >= this.motionBudgetMs) {
      this.elapsedMs = this.motionBudgetMs;
      this.oldValue = null; // motion budget spent: discard the retained old snapshot
    }
  }
}

/** Fallback fill colours by drawable kind, used to tint a wide-tier marker `Graphics`
 * when its `--colony-N` token can't be resolved and to colour the un-colonied markers
 * (patch bloom, ruin). The close-tier / anchor kinds keep an entry so the record stays
 * total over `WorldDrawableKind`, but those kinds draw textured sprites, not markers. */
const PLACEHOLDER_COLOR: Record<WorldDrawableKind, number> = {
  bee: 0xf2b90c, // waggle amber
  blob: 0xe08a1e,
  queen: 0xd6396b,
  patch: 0x6fae4a,
  ruin: 0x6b6155,
  hive: 0x9a6b3f, // comb brown (the static own-hive anchor tile, D4)
  'own-queen': 0xd6396b, // the own-queen anchor sprite (shares the queen tint)
};

const MOTION_BUDGET_MS = 240; // UI.md §2: no fades longer than 240ms

/** Fallback fill when a `var(--colony-N)` colour token can't be resolved (no DOM,
 * or the token isn't set) — never a crash, never a blank marker. */
const COLONY_COLOR_FALLBACK = 0xe08a1e;

/**
 * Resolves a `var(--colony-N)` token string to a Pixi fill colour by reading the
 * live computed style off `document.documentElement` (the same token the React DOM
 * surfaces — leaderboard dot, etc. — read directly as a CSS value). Falls back to
 * `fallback` when `color` is absent, not a `var(...)` token, or the token resolves
 * to nothing (no DOM in a unit-test environment, token not yet defined). Not unit
 * tested — `CanvasRenderer` itself is manual/on-device only (D10); the *decision*
 * of which token to use is `buildDrawables`' pure, tested concern.
 */
function resolveFillColor(color: string | undefined, fallback: number): number {
  if (!color || typeof document === 'undefined') {
    return fallback;
  }
  const match = /^var\((--[\w-]+)\)$/.exec(color);
  if (!match) {
    return fallback;
  }
  const raw = getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(raw)) {
    return fallback;
  }
  return Number.parseInt(raw.slice(1), 16);
}

/**
 * The production `AssetLoader`: Pixi's global `Assets.load` over the manifest, one
 * `Texture` per alias (design "Directional sprite asset registry"). A GPU/browser path
 * exercised on-device only (D-086); node tests inject the group-1.2 fake loader instead
 * of this, so `Assets` is never touched under test.
 */
function createPixiAssetLoader(): AssetLoader<AssetManifest, AssetBundle> {
  return {
    async load(manifest: AssetManifest): Promise<AssetBundle> {
      // Annotate as `UnresolvedAsset[]` so overload resolution picks the aliased-bundle
      // form (`Promise<Record<alias, Texture>>`), not the single-asset form.
      const requests: UnresolvedAsset[] = Object.entries(manifest).map(([alias, src]) => ({
        alias,
        src,
      }));
      const bundle = await Assets.load<Texture>(requests);
      return bundle as AssetBundle;
    },
  };
}

/** The directional-sheet cell size (24×24, 12 headings across = a 288×24 sheet, UI.md §8).
 * The `worker` fly/walk sheets and the fixed-frame own-queen sheet are frame-sliced by this;
 * the flower + hive textures are whole single images. */
const FRAME_SIZE = 24;

/** Resting hover height (screen px, pre-depth-scale) a flying bee floats above its offset
 * shadow in `full` mode; the hover-bob oscillates about it. */
const BASE_HOVER = 3;

/** The offset-shadow x-shift (screen px, pre-scale) that reads as a bee floating above the
 * ground rather than sitting on it (design D2). */
const SHADOW_OFFSET_X = 2;

/** How far DOWN (screen px, pre-scale) both a bee's sprite and its shadow are shifted, so the
 * shadow ellipse reads as being *under and around* the bee's feet rather than entirely below
 * them (2026-08-11 request: "move the bee down so that the sprite is centred on the shadow").
 *
 * Applied to the sprite and the shadow **together**, which is what keeps a flying bee's visible
 * hover gap exactly `BASE_HOVER + bob` — the same jump it had before this shift existed.
 *
 * A presentation constant, deliberately separate from `BASE_HOVER`: this one says where the
 * ground contact sits, that one says how high a flying bee floats above it. */
const GROUND_SINK = 2;

/** Standing shadows sit four source pixels higher than the former shared ground line: twice the
 * ellipse's two-pixel radius, so the bee now visibly stands over rather than above its shadow. */
export function shadowYFor(cue: Exclude<DepthCue, 'none'>): number {
  return cue === 'ground-shadow' ? GROUND_SINK - 4 : GROUND_SINK;
}

/** A dead bee's full-body grey tint (design "Dead-bee corpse presentation") — a mid-grey
 * multiply over the sprite's natural colours, distinct enough from every live type/colony
 * tint that a corpse reads as "greyed out" at a glance. */
const DEAD_BEE_TINT = 0x8c8c8c;

/** The damage-flash colour pair (design E7/E8, prototype Finding 4), ported as Pixi hex ints
 * from `prototype/tokens/colors.css`'s `--damage-flash` / `--damage-flash-core` — the same
 * "a canvas has no CSS cascade to read from" precedent `DEAD_BEE_TINT`/`colonyPalette`'s
 * numeric constants already set. Deliberately its OWN pair, never `--danger`: `--danger` is
 * byte-identical to `--colony-1`/`--bee-warrior`, so flashing a colony-1 bee in it would be
 * near a no-op and flashing any other colony in it would briefly assert a false colony
 * identity — beyond the collision, `--danger` means "an error in the interface", and a bee
 * taking a hit is the game working correctly. */
const FLASH_COLOR = 0xff4a2e; // --damage-flash
const FLASH_CORE_COLOR = 0xfff1ec; // --damage-flash-core

/**
 * The body tint's weight relative to the flash's peak.
 *
 * **1.0, reversing prototype Finding 3 (2026-08-11 request).** This was 0.35 with an outline halo
 * carrying the read — "outline primary, body tint secondary" — precisely so a flashing bee kept its
 * form, heading and colony identity, and the comment here recorded that a weight of 1.0 "would
 * reproduce the rejected full-body-tint failure". The halo has now been removed and the whole bee
 * flashes, which is that rejected treatment, adopted deliberately: on a projector the ring read as
 * a red circle stuck to the bee rather than as damage.
 *
 * What it costs, stated rather than discovered later: for the flash's ~200 ms a struck bee is not
 * attributable to its colony by colour. That is acceptable in the demo (one colony plus one raider)
 * and a real, accepted regression at a 150-colony endgame.
 */
const FLASH_BODY_TINT_WEIGHT = 1.0;

/** How long (ms) the flash's near-white core phase lasts before settling to `FLASH_COLOR`
 * (design E7/E8, prototype Finding 3: "near-white core over the first 40 ms"). */
const FLASH_CORE_MS = 40;

/**
 * The core-phase threshold, expressed as an intensity rather than an elapsed duration: the
 * paint layer only ever sees `DamageFlashTracker`'s decaying 0..1 intensity (never elapsed ms
 * directly), and that intensity falls linearly from 1 to 0 across `DEFAULT_FLASH_MS` (the
 * wall-clock case — the same linear shape the tick-anchored case also produces, `intensityOf`).
 * The first `FLASH_CORE_MS` of that window is therefore intensity ≥ `1 − FLASH_CORE_MS /
 * DEFAULT_FLASH_MS`, derived rather than a second hand-picked literal so the two constants
 * cannot silently drift apart.
 */
const FLASH_CORE_INTENSITY_THRESHOLD = 1 - FLASH_CORE_MS / DEFAULT_FLASH_MS;

/** Linear interpolation between two 0xRRGGBB ints at `t` (0..1, clamped) — the pure colour
 * mixer `flashPaintFor` uses to derive the reduced-alpha secondary body tint. Exported for
 * unit testing (D-086: the arithmetic is pure, only the `Graphics`/`Sprite` calls that consume
 * it are GPU-real). */
export function lerpColor(from: number, to: number, t: number): number {
  const clamped = Math.max(0, Math.min(1, t));
  const fr = (from >> 16) & 0xff;
  const fg = (from >> 8) & 0xff;
  const fb = from & 0xff;
  const tr = (to >> 16) & 0xff;
  const tg = (to >> 8) & 0xff;
  const tb = to & 0xff;
  const r = Math.round(fr + (tr - fr) * clamped);
  const g = Math.round(fg + (tg - fg) * clamped);
  const b = Math.round(fb + (tb - fb) * clamped);
  return (r << 16) | (g << 8) | b;
}

/** One drawable's resolved flash paint — or `null` when there is nothing to paint.
 *
 * `overlayColor`/`overlayAlpha` are the **primary** carrier since 2026-08-11: an additive-blended
 * copy of the bee's own sprite, painted OVER the body, so the flash takes the bee's exact
 * silhouette. (It was a stroked ellipse halo painted *beneath* the body until then; see
 * `FLASH_BODY_TINT_WEIGHT`.) `bodyTintColor` is the multiply applied to the body itself. */
export interface FlashPaint {
  readonly overlayColor: number;
  readonly overlayAlpha: number;
  readonly bodyTintColor: number;
}

/**
 * Resolves a drawable's damage-flash paint: `null` for a `dead` corpse — **explicit**
 * precedence, so a stray `flashIntensity` a caller somehow attached to a dead drawable is refused
 * rather than silently painted, even though `corpseDrawables` structurally never sets one — and
 * `null` for anything with no active (`undefined` or non-positive) intensity.
 *
 * Otherwise the bee **flashes whole**: the overlay colour switches to the near-white
 * `FLASH_CORE_COLOR` for the first `FLASH_CORE_MS` of the presentation window
 * (`FLASH_CORE_INTENSITY_THRESHOLD`) and `FLASH_COLOR` after it — a red/white pulse — with the
 * alpha scaling by intensity, and the body tint multiplying toward `FLASH_COLOR` underneath.
 *
 * Pure — the same D-086 split as `depthCueFor`; the `drawTier` body that turns this into
 * `Sprite` calls is GPU-real.
 */
export function flashPaintFor(drawable: WorldDrawable): FlashPaint | null {
  if (drawable.dead === true) return null;
  return flashPaintAt(drawable.flashIntensity);
}

/** The intensity → paint arithmetic on its own, shared by the bee path ({@link flashPaintFor})
 * and the hive-cell path ({@link Renderer.drawHiveCells}) so a struck cell pulses with exactly
 * the same colour ramp a struck bee does — one treatment, one place to tune it. Cells have no
 * `dead` state (a destroyed cell leaves the wire's cell set entirely), so the corpse precedence
 * stays on the bee-facing wrapper. */
export function flashPaintAt(intensity: number | undefined): FlashPaint | null {
  if (intensity === undefined || intensity <= 0) return null;
  const overlayColor = intensity >= FLASH_CORE_INTENSITY_THRESHOLD ? FLASH_CORE_COLOR : FLASH_COLOR;
  return {
    overlayColor,
    overlayAlpha: intensity * FLASH_PEAK_ALPHA,
    bodyTintColor: lerpColor(0xffffff, FLASH_COLOR, intensity * FLASH_BODY_TINT_WEIGHT),
  };
}

/** The flash overlay's alpha at the tracker's peak (freshly spawned, intensity 1.0) — short of
 * full opacity, so even at peak the bee's form is still legible through the flash. */
const FLASH_PEAK_ALPHA = 0.7;

/** Stroke width (screen px) of a struck hive cell's flash, drawn on the hex's own edge. */
const HIVE_CELL_FLASH_STROKE_WIDTH = 3;

/** The selected bee's overlay colour — a bright blue, deliberately outside the game's warm
 * honey/ink palette and outside every `--colony-N` value, so a selection can never be mistaken for
 * a colony identity or for damage. Replaces the demo's two-ring DOM circle (2026-08-11 request:
 * "a bright blue outline around them rather than the circle"). */
const SELECTION_OUTLINE_COLOR = 0x2ea8ff;

export interface SelectionPaint {
  readonly color: number;
  readonly alpha: number;
}

/** Pure selected-bee paint decision. Full presentation pulses on a one-second injected-clock
 * cycle; degraded/reduced presentation remains informative at one stable intensity. */
export function selectionPaintFor(
  drawable: Pick<WorldDrawable, 'selected'>,
  nowMs: number,
  animMode: AnimationMode,
): SelectionPaint | null {
  if (drawable.selected !== true) return null;
  const alpha =
    animMode === 'full'
      ? 0.45 + 0.2 * Math.sin((nowMs / 1000) * Math.PI * 2)
      : 0.45;
  return { color: SELECTION_OUTLINE_COLOR, alpha };
}

/** The explicit compositing contract used by the sprite host: combat feedback is always topmost. */
export function beePaintLayers(input: {
  selected?: boolean;
  flashIntensity?: number;
}): readonly ('body' | 'selection' | 'damage-flash')[] {
  const layers: ('body' | 'selection' | 'damage-flash')[] = ['body'];
  if (input.selected === true) layers.push('selection');
  if (input.flashIntensity !== undefined && input.flashIntensity > 0) layers.push('damage-flash');
  return layers;
}

/** Patch nectar meter bar height (screen px, pre-camera-scale, flower-attach-meter finding 11)
 * and its rise above the patch's projected anchor point — small enough to read as a status
 * chip over the ~16×20 px flower sprite, not a competing shape. The bar's width is
 * `patch-meter.ts`'s exported `METER_BAR_WIDTH_PX` — the single source of truth shared with
 * that module's own legibility threshold, so "wide enough to draw" and "wide enough to
 * suppress" never drift apart. */
const METER_BAR_HEIGHT_PX = 3;
const METER_BAR_RISE_PX = 22;

/** Sustained fps below which the degradation monitor's frame-rate guard trips (design D7):
 * under the UI.md §11 non-regression bar, drop to `reduced` mode (no hover-bob/shadow) to
 * recover the budget. The monitor's measurement window + anti-flap hysteresis keeps a few
 * slow frames from flapping the mode. */
const DEGRADE_BELOW_FPS = 30;

/** One entity's persistent display state in a tier layer (design D10) — the per-entity map
 * value that replaces the old `Map<string, Graphics>`. `node` is the container entered into
 * the y-sorted `world` layer and GC'd on disappear; `body` is a directional `Sprite` for the
 * textured kinds (bee / close-tier patch / hive / own-queen) or a marker `Graphics` for the
 * wide-tier kinds; `shadow` is the lazily-created flying-bee offset shadow / walking ground
 * line. `appliedSheet`/`appliedFrame` record the texture currently assigned so a `Sprite`
 * body is mutated in place (its texture reassigned only when the gait/heading selection
 * changes), never rebuilt each frame. */
interface EntityNode {
  readonly kind: WorldDrawableKind;
  readonly node: Container;
  readonly body: Sprite | Graphics;
  shadow: Graphics | null;
  /** The lazily-created damage-flash outline halo (combat-strikes-and-damage-flash, design
   * E7/E8) — a stroked ellipse beneath the body, mirroring `shadow`'s own lazy-create/child-
   * ordering pattern. `null` until the entity's first active flash. */
  /** The damage flash: an additive copy of the body's own texture, painted OVER it. */
  flashOverlay: Sprite | null;
  /** The selection overlay: a same-size blue copy of the body's own texture, above the body and
   * below damage flash. */
  selectionSilhouette: Sprite | null;
  appliedSheet: AssetKey | null;
  appliedFrame: number;
  /** The `BeeTint.key` of the palette variant currently assigned, or `null` for the raw
   * sheet — the third component of the mutate-in-place guard, so a bee that changes type (or
   * whose viewer gains an own colony) reassigns its texture, and one that does not never
   * touches it. */
  appliedTint: string | null;
}

/**
 * Realises one palette-swapped variant of a bee sheet (bee-body-palette): decodes the loaded
 * texture's own pixels through a 2D canvas, runs the pure {@link recolorBeePixels}, and wraps
 * the result back into a `Texture`.
 *
 * The canvas/`ImageData` round-trip is chosen over Pixi's `renderer.extract` GPU readback
 * because the source pixels are ALREADY in memory as the decoded image resource (no second
 * fetch, no GPU sync point), and because `ImageData.data` is exactly the `Uint8ClampedArray`
 * the pure recolour function takes — no format shim. The variant inherits the source's scale
 * mode so the pixel-art sampling is unchanged.
 *
 * Called once per `(sheet, tint.key)` variant at first use and cached by the caller — never
 * per bee, never per frame. Returns `null` if the recolour is not possible (no DOM, an
 * undrawable resource), so the caller falls back to the plain amber sheet — never a crash, a
 * blank bee, or a mistaken claim of ownership over an `Assets`-owned texture.
 * GPU/browser wiring, device-exercised only (D-086).
 */
function recolorSheet(source: Texture, tint: BeeTint): Texture | null {
  try {
    // `pixelWidth`/`pixelHeight` (not `width`/`height`, which are resolution-divided) — the
    // recolour is a literal per-pixel pass, and `frameTexture` slices the variant in the same
    // real pixel units.
    const width = source.source.pixelWidth;
    const height = source.source.pixelHeight;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx === null) {
      return null;
    }
    ctx.drawImage(source.source.resource as CanvasImageSource, 0, 0);
    const image = ctx.getImageData(0, 0, width, height);
    ctx.putImageData(new ImageData(recolorBeePixels(image.data, tint), width, height), 0, 0);
    // `skipCache` — this texture belongs to THIS renderer and is destroyed with it. The colony
    // underlay remounts its canvas (and so its renderer) on every container resize, so letting
    // variants accumulate in Pixi's global texture cache would leak one set per remount.
    const variant = Texture.from(canvas, true);
    variant.source.scaleMode = source.source.scaleMode;
    return variant;
  } catch {
    return null;
  }
}

export class CanvasRenderer {
  /** The y-sorted entity layer (bees, markers, anchor) — children sort by `zIndex`. */
  private readonly world = new Container();
  /** The season ground layer, behind the entity layer: two flat colour fills (current +
   * the outgoing colour cross-faded on a season change, design D5, "flat season-tinted
   * fill"). */
  private readonly groundView = new Container();
  /** World-space ground scatter (field-ground-scatter): grass tufts and rocks, its own
   * layer beneath `world` rather than joining the y-sorted entity list — simpler and
   * cheaper (no per-frame sort against thousands of bees) since scatter never needs to
   * interleave with entities, only sit behind all of them; see `syncScatter`. */
  private readonly scatterLayer = new Container();
  /** Persistent per-item scatter sprites, keyed by `ScatterItem.key` (never the viewport),
   * so a sprite already on screen survives a small pan/zoom instead of being torn down and
   * recreated (`syncScatter`'s create/mutate/destroy diff, mirroring `drawTier`'s). */
  private readonly scatterSprites = new Map<string, Sprite>();
  private readonly ring = new Graphics();
  /** The dashed play-boundary rectangle (`deathmatch-world` D-6) — its own layer for the same
   * reason the bloom ring has one: a primitive world-space outline, cleared and restroked each
   * frame, that must not be interleaved with entity sprites. */
  private readonly playBoundary = new Graphics();
  private crossfade: CrossfadeManager<WorldSnapshot> | null = null;
  private readonly sprites = new Map<string, EntityNode>();
  private readonly oldSprites = new Map<string, EntityNode>();
  /** Cached frame sub-textures sliced from the directional sheets, keyed
   * `sheet:tintKey:frame`, so a heading swap reuses one texture per (sheet, variant, frame)
   * rather than allocating per draw. */
  private readonly frameCache = new Map<string, Texture>();
  /** Palette-swapped whole-sheet variants (bee-body-palette), keyed `sheet:tintKey` — the
   * cache that makes the CPU recolour a once-per-variant cost rather than a per-bee or
   * per-frame one. Keyed on the tint's RESOLVED colours, so two types that happen to share a
   * colour share one variant. Bounded by (bee sheets × distinct colours in play): at most 3
   * sheets × (12 own type colours + 8 colony colours) even in a maximal match. */
  private readonly variantSheets = new Map<string, Texture>();
  /** The directional-sprite texture registry (design "Directional sprite asset
   * registry"). `init()` awaits its `load()` before starting the loop, so the first sprite
   * draw can never reference an unloaded texture; the draw body gates on `assetsReady`.
   * The loader is injectable for testing (production wraps Pixi `Assets`). */
  private readonly assets: AssetRegistry;

  // The group 4–10 pure/tested seams this GPU body composes.
  /** Client-side, thrash-stabilized facing (design D3, group 5): fed one accepted
   * close-tier frame per `render`, read per drawable for the bee's directional frame. */
  private readonly facing = new FacingDeriver();
  /** Delayed, resettable presentation clock (design D8, group 10): buffers per-entity tick
   * samples, interpolates bee positions at the delayed target, and drives hover-bob.
   * **wire-required** (client-tick-rate-provenance, tick-rate-single-source): constructed in
   * the constructor body, not as a field initializer, because it needs the constructor's
   * required `tickRateHz` parameter — there is no build default to fall back to. */
  private readonly presentClock: PresentationClock;
  /** Per-season ground model (design D5, group 7): current/old tile + crossfade timing. */
  private readonly ground = new GroundLayer(LIVE_PLAY_RENDER_INPUT.season);
  /** Anti-flap degradation state machine (design D7, group 9) → the `reduced` mode.
   * Injectable (design D-E, client-rendering): defaults to the frame-guarded monitor this
   * renderer has always built (`resolveDegradationMonitor`), but a caller — the device
   * benchmark — may supply one that never degrades, so its workload cannot get cheaper
   * because the device it is measuring is slow. */
  private readonly degradation: DegradationMonitor;
  /** Dead-bee corpse lifecycle (design "Dead-bee corpse presentation", bee-visual-states
   * bug c): fed one accepted close-tier frame per `render` (classifying a disappearance as
   * churn / viewport-exit / death), aged in wall-clock time per RAF frame via `advance`.
   * **wire-required** (client-tick-rate-provenance, tick-rate-single-source): its hold/fade
   * tick windows are derived from the constructor's required `tickRateHz`, so — like
   * `presentClock` — it is constructed in the constructor body rather than as a field
   * initializer. */
  private readonly corpses: CorpseTracker;
  /** The damage-flash classifier/presentation tracker (combat-strikes-and-damage-flash,
   * design E4/E7/E8): fed one accepted close-tier frame per `render` (bee health / cell hp
   * bucket diffs), aged per RAF via `advance`. **wire-required** for the same reason as
   * `presentClock`/`corpses` above — its decay window is derived from the constructor's
   * required `tickRateHz`, so it too is constructed in the body, not as a field initializer. */
  private readonly flashes: DamageFlashTracker;
  /** The dance-visuals overlay tracker (dance-visuals): watcher set derived once per accepted
   * close frame, drawables produced per RAF. */
  private readonly dances = new DanceTracker();
  /** Every gathering bee's flower anchor (flower-attach-meter review finding), derived ONCE
   * per accepted close frame (`attachmentsForFrame`) — the same "derived once per accepted
   * frame, reused every RAF" pattern as `facing`/`presentClock`/`dances` above
   * (architecture-review A2) — rather than the O(bees × patches) scan `buildDrawables` ran
   * inline on every RAF frame regardless of whether a new snapshot had actually arrived.
   * Cleared on a wide frame (close-tier-only concept, mirrors `corpses`/`dances.clear()`). */
  private beeAttachments: ReadonlyMap<number, readonly [number, number]> = new Map();
  /** The dance-overlay `Graphics` layer, redrawn each frame like the bloom `ring` (a
   * self-contained primitive paint, not the sprite node-diff; GPU paint device-exercised
   * only, D-086). */
  private readonly danceOverlay = new Graphics();

  /** The per-bee health/nectar bar layer (bee-body-palette, playtest finding 10): ONE
   * `Graphics` redrawn per frame for every bee that shows a bar — the same batched-primitive
   * pattern as `danceOverlay`/`hiveCellOverlay`, chosen over a per-bee `Graphics` (or a
   * per-bee sprite pair) because the endgame is thousands of close-tier bees and a node per
   * bee per bar would triple the scene graph. GPU paint, device-exercised only (D-086). */
  private readonly beeBarOverlay = new Graphics();

  /** The in-world state-label layer (WAG-36) — the canvas's FIRST text, and its own container
   * above {@link beeBarOverlay}. One `Sprite` per drawn label over a texture cached per
   * distinct state name, pooled and reused across frames: never a `Text` object per bee, and
   * no bitmap-font asset. GPU paint, device-exercised only (D-086); every decision it paints
   * is the pure `state-labels.ts` seam. */
  private readonly stateLabelLayer = new Container();
  private readonly stateLabelSprites: Sprite[] = [];
  private readonly stateLabels = new StateLabelPainter<Texture>(
    (name) => this.createLabelTexture(name),
    (texture) => {
      // `Texture.EMPTY` is the shared no-DOM fallback and is not ours to destroy.
      if (texture !== Texture.EMPTY) {
        texture.destroy(true);
      }
    },
  );
  private stateLabelPlayback: StateLabelPlayback = { subjectColonyId: null };

  /** The cell-by-cell hive comb (hive-egg-cells): one pooled `Sprite` per visible cell,
   * stamped from the `hiveCells` state sheet, redrawn each frame from the close-tier wire
   * cells via the pure `hiveCellDrawables` seam. Sprites rather than the flat `Graphics`
   * hexes this used to paint, so the comb has wax walls, a bore, and a brood egg that sits
   * *in* the cell — the tiles are y-sorted by `zIndex` because they are 3D and a near tile's
   * rim must occlude the one behind it. GPU device-exercised only (D-086). */
  private readonly hiveCellLayer = new Container();
  private readonly hiveCellSprites: Sprite[] = [];
  /** Marks cells that have just appeared, so they present as under construction (the crane
   * frame) for a moment — the wire carries no build-progress flag, so first sight is the
   * only honest signal there is. */
  private readonly freshCells = new FreshCellTracker();
  /** The damage-flash stroke over the comb (combat-strikes-and-damage-flash): still a
   * primitive, because it composes OVER whichever tile the cell drew and must not become a
   * seventh sheet frame. */
  private readonly hiveCellOverlay = new Graphics();
  /** The **symbol-mode** `Graphics` layer (`symbols.ts`): below the symbol zoom threshold,
   * bees and flower patches draw as one batched set of colour dots instead of sprites — the
   * same primitive-paint pattern as `danceOverlay`/`hiveCellOverlay`, and for the same
   * reason it beats a node per entity (the endgame is thousands of close-tier bees, and
   * symbol mode is precisely the zoom at which there are most of them on screen). GPU paint,
   * device-exercised only (D-086). */
  private readonly symbolOverlay = new Graphics();

  /** The patch-nectar-meter `Graphics` layer (flower-attach-meter, finding 11), redrawn each
   * frame from the pure `patchMeterDrawables` seam — one batched `Graphics` for every
   * non-full patch's bar, never one `Graphics` instance per patch, following the
   * `danceOverlay`/`hiveCellOverlay` primitive-paint pattern; GPU paint device-exercised only
   * (D-086). */
  private readonly patchMeterOverlay = new Graphics();
  /** The RAF loop over injected scheduler/clock (design D8, group 10). */
  private readonly loop: RenderLoop;
  private groundCurrent: Graphics | null = null;
  private groundOld: Graphics | null = null;
  private groundGrainCurrent: TilingSprite | null = null;
  private groundGrainOld: TilingSprite | null = null;
  private readonly groundGrainTextures = new Map<AssetKey, Texture>();

  // Latest inputs supplied by `render()` and read by the RAF loop each frame, so an
  // independent (non-world) lifecycle / season / epoch change reaches the draw body within
  // one frame even while the stream stalls (design D9).
  private latestWorld: WorldSnapshot | null = null;
  private latestCamera: Camera | null = null;
  private latestWide: WideColonyContext | undefined = undefined;
  private latestRing: BloomRingGeometry | null = null;
  private lastRenderInput: RenderInput = LIVE_PLAY_RENDER_INPUT;
  /** Wall-clock of the previous RAF frame — the inter-frame delta for the ground crossfade
   * and the tier crossfade advance. */
  private lastFrameMs: number | null = null;
  private initialized = false;

  /**
   * @param tickRateHz The running sim tick rate (Hz) — **wire-required**
   *   (client-tick-rate-provenance, tick-rate-single-source): sourced from
   *   `Welcome.tick_rate_hz` / `manifest.tickRateHz` by the caller. No default; passing no rate
   *   is a compile error rather than a silently substituted build default, because a renderer
   *   mounted against a non-default-rate server would otherwise present its delayed
   *   interpolation target and its corpse hold/fade windows at the wrong wall-clock speed.
   * @param degradationMonitor The degradation monitor to use (design D-E, client-rendering
   *   "the renderer's degradation monitor is injectable"). Omit for production/play — the
   *   renderer then builds exactly the frame-guarded monitor it always has
   *   (`resolveDegradationMonitor`), so this parameter is additive and changes no shipped
   *   behaviour. A caller that must hold the animation mode fixed regardless of frame rate
   *   (the device benchmark) supplies its own.
   * @param app The pixi application. Injected for the same reason the loader/clock/scheduler
   *   are: jsdom has no WebGL, so a real `Application.init()` rejects and every statement of
   *   {@link init} past its first `await` — including the whole cancellation window (D-290) —
   *   is otherwise unreachable from any automated test. The default expression is also the
   *   compile-time conformance check between `Application` and `PixiApp`. An INSTANCE rather
   *   than a factory: one renderer owns exactly one application for its whole life, so there
   *   is no re-creation for a factory to parameterise.
   */
  constructor(
    private readonly container: HTMLElement,
    tickRateHz: number,
    loader: AssetLoader<AssetManifest, AssetBundle> = createPixiAssetLoader(),
    private readonly clock: Clock = performanceClock,
    scheduler: FrameScheduler = rafFrameScheduler,
    degradationMonitor?: DegradationMonitor,
    private readonly app: PixiApp = new Application(),
  ) {
    this.assets = new AssetRegistry(loader);
    this.degradation = resolveDegradationMonitor(this.clock, degradationMonitor);
    this.loop = new RenderLoop(scheduler, this.clock);
    this.presentClock = new PresentationClock({ tickRateHz });
    this.corpses = new CorpseTracker({ tickRateHz });
    this.flashes = new DamageFlashTracker({ tickRateHz });
  }

  /** Updates interpolation playback only; corpse/flash/simulation tick windows retain the fixed
   * constructor rate. */
  setPresentationRate(rate: number): void {
    this.presentClock.setPlaybackRate(rate, this.clock.now());
  }

  /** Whether the directional-sprite manifest has loaded (design "Directional sprite
   * asset registry"): the sprite draw body gates on this so no draw references an unloaded
   * texture. False until `init()`'s awaited `load()` resolves. */
  get assetsReady(): boolean {
    return this.assets.ready;
  }

  /** The most recent render-input context (design D9), exposed for on-device inspection;
   * live-play until the first `render`. */
  get renderInput(): RenderInput {
    return this.lastRenderInput;
  }

  /**
   * Builds the pixi application, attaches its canvas, loads the sprite manifest and starts the
   * render loop.
   *
   * @param signal Cancels a construction that has been superseded (D-290). **Optional, and
   *   deliberately so**: every host-facing renderer seam declares `init(): Promise<void>`, and
   *   TypeScript accepts a one-parameter function where a zero-parameter one is required — so
   *   this stays assignable to all of them, and no existing test double needs to change.
   *
   * **Cancellation resolves; it never rejects.** A host's rejection handler reports "the
   * renderer failed to start", and StrictMode's double-mount, every back-navigation and every
   * remount cancel a run — so rejecting would turn ordinary lifecycle churn into a console full
   * of start-up failures, and would make cancellation indistinguishable from a genuinely absent
   * WebGL context. The caller's own cancellation flag stays the sole authority on what happens
   * next. A NON-cancelled failure still rejects exactly as before; `ReviewFrame` reasons about
   * that path explicitly and depends on it.
   *
   * **Cancellation is reclaimed at once, not when the in-flight await settles.** That is the
   * difference between closing D-290 and moving it one await later: the sprite-manifest load is
   * the long wait, and two of the five renderer hosts (`AmbientBackground`, `DemoStage`) have no
   * second teardown that would collect an orphan afterwards — for them "eventually" never
   * arrives, and a hung load leaks a live WebGL context for the life of the page.
   */
  async init(signal?: AbortSignal): Promise<void> {
    if (this.initialized) {
      // Idempotent — one Pixi app + one render loop per renderer. **A renderer whose construction
      // failed or was cancelled is spent, not retryable**: the flag is raised before the first
      // await, so a second `init()` on such an instance no-ops rather than rebuilding. That is
      // deliberate and is the whole contract — a renderer is a per-construction object, and all
      // five hosts build a fresh one per run. Clearing the flag on failure would add a
      // set/clear matrix across the abort, failure and destroy paths to serve no caller that
      // exists. The one case that must stay retryable is a construction cancelled *before it
      // began*, which never raises the flag at all (see below).
      return;
    }
    if (signal?.aborted) {
      // Cancelled before we touched pixi at all: build nothing, and leave `initialized` false
      // so the flag never claims a renderer that does not exist.
      return;
    }
    this.initialized = true;
    // **The one teardown path for an unfinished construction — and it can never itself become
    // the failure.** `destroy()` on a half-built renderer may throw (it walks layers and textures
    // that may not exist yet), and there are two places that would turn such a throw into a
    // *worse* bug than the one it was cleaning up after: this function is installed as an `abort`
    // listener, where a throw is an uncaught exception in whatever code called `abort()`; and it
    // runs on the catch path, where a throw would make a CANCELLED `init()` reject and break the
    // contract above outright. `ReviewFrame` already guards its own teardown for exactly this
    // reason. Reported under its own message so a teardown failure is never mistaken for a
    // start-up failure.
    const reclaim = (): void => {
      try {
        this.destroy();
      } catch (teardownError: unknown) {
        console.error(
          'waggle: the renderer failed to tear down an unfinished construction',
          teardownError,
        );
      }
    };
    // Reclaim on abort without waiting for the in-flight await to settle.
    //
    // **What this does and does not cover, stated precisely.** The window it genuinely closes is
    // `assets.load()` — the long wait, with the canvas already attached and the context live.
    // During `app.init()` it can reclaim nothing, and the earlier claim here that it could was
    // wrong: pinned pixi's `Application.init()` is `this.renderer = await autoDetectRenderer(...)`,
    // so the renderer is owned by an unresolved promise *inside* pixi and is not exposed on the
    // application until that inner await finishes — `destroy()`'s `this.app.renderer` guard
    // therefore sees null and reclaims nothing. Check B below collects it the instant it IS
    // exposed. The listener is still installed here rather than after B: it costs nothing, it
    // covers the window between pixi exposing the renderer and our own continuation being
    // scheduled, and it does not depend on pixi keeping that internal shape.
    signal?.addEventListener('abort', reclaim, { once: true });
    try {
      await this.buildInto(signal, reclaim);
    } catch (error: unknown) {
      // **Unwound on EVERY failure, not only a cancelled one.** A rejecting `assets.load()`
      // rejects with the canvas already attached and the context live, and the hosts do not all
      // clean that up: `AmbientBackground` and `DemoStage` only log the rejection, and
      // `InMatchCanvas`/`Match` had no rejection handler at all. Unwinding here makes the
      // renderer responsible for its own half-built state instead of relying on five hosts to
      // agree about it. `reclaim` swallows its own failure, so the ORIGINAL error is always the
      // one that reaches the caller.
      reclaim();
      if (signal?.aborted) {
        // An await that rejected AFTER cancellation would otherwise propagate — reporting an
        // ordinary teardown as a start-up failure.
        return;
      }
      throw error; // a genuine failure (no WebGL context, a failed sheet load) — still rejects
    } finally {
      // So a settled renderer is not retained by a long-lived signal, and so an abort raised
      // AFTER a clean init is a no-op rather than a retroactive teardown.
      signal?.removeEventListener('abort', reclaim);
    }
  }

  /**
   * `init()`'s body, split out so the cancellation bookkeeping above reads as one thing.
   *
   * The two `signal?.aborted` checks are **not** redundant with the `abort` listener: the
   * listener *reclaims*, these *stop this method from continuing to build*. Check B also does
   * the reclaiming outright for a cancellation that landed during `app.init()`, where the
   * listener could not (see `init()`'s note on pixi's internal `autoDetectRenderer` await).
   * `reclaim` is idempotent and self-guarding, so overlapping calls cost nothing.
   */
  private async buildInto(signal: AbortSignal | undefined, reclaim: () => void): Promise<void> {
    await this.app.init({
      resizeTo: this.container,
      backgroundAlpha: 0,
      antialias: false,
      roundPixels: true,
    });
    if (signal?.aborted) {
      // Check B. Returns BEFORE attaching the canvas: a cancelled run must never put one in the
      // host, and must never enter the sprite load at all. This is also the first moment pixi has
      // exposed its renderer, so this is where a cancellation that landed during `app.init()` is
      // actually reclaimed.
      reclaim();
      return;
    }
    this.app.canvas.style.imageRendering = 'pixelated';
    this.container.appendChild(this.app.canvas);
    // Ground behind (lowest zIndex), entity layer in the middle, bloom ring on top; the
    // entity layer sorts its children by depth (`zIndex = screenY`), the ground layer sorts
    // the outgoing tile above the incoming one during a crossfade.
    this.groundView.zIndex = -1000;
    this.groundView.sortableChildren = true;
    // World-space ground scatter (field-ground-scatter) sits above the flat colour fill
    // but below everything else static/dynamic — bees, flowers, the hive, and the
    // cell-by-cell hive composition all draw over it.
    this.scatterLayer.zIndex = -900;
    this.world.zIndex = 0;
    this.world.sortableChildren = true;
    // The patch meter (flower-attach-meter) floats above the entity layer — like the dance
    // cues — so a bar is never hidden beneath a bee's sprite/shadow standing on the flower.
    // It sits one step below the bee bars: a bee draws over the flower it stands on, so its
    // own readout should too.
    this.patchMeterOverlay.zIndex = 998;
    // Bee bars sit above every entity but below the bloom ring and the dance cues: they are
    // a per-bee readout, not a match-level signal, and must never occlude a dance arrow.
    this.beeBarOverlay.zIndex = BEE_BAR_OVERLAY_Z;
    this.ring.zIndex = 1000;
    // Below the bloom ring and the dance cues, above the ground and the comb: it is a
    // world-edge marker, never something that should occlude a match-level signal.
    this.playBoundary.zIndex = 999;
    this.danceOverlay.zIndex = 1001; // dance cues float above the bloom ring and entities
    // The cell-by-cell hive (hive-egg-cells) composes BELOW the entity layer — it is ground
    // structure the bees walk over — but above the season ground.
    this.hiveCellLayer.zIndex = -501;
    this.hiveCellLayer.sortableChildren = true; // back-to-front within the comb
    this.hiveCellOverlay.zIndex = -500;
    this.app.stage.addChild(this.groundView);
    this.app.stage.addChild(this.scatterLayer);
    this.app.stage.addChild(this.playBoundary);
    this.app.stage.addChild(this.hiveCellLayer);
    this.app.stage.addChild(this.hiveCellOverlay);
    this.app.stage.addChild(this.world);
    // Symbols stand in FOR the entity sprites, so they sit with them — above the comb and
    // the ground, below the per-bee bars and the match-level cues.
    this.symbolOverlay.zIndex = 500;
    this.app.stage.addChild(this.symbolOverlay);
    this.app.stage.addChild(this.patchMeterOverlay);
    this.app.stage.addChild(this.beeBarOverlay);
    this.app.stage.addChild(this.ring);
    this.app.stage.addChild(this.danceOverlay);
    this.stateLabelLayer.zIndex = STATE_LABEL_LAYER_Z;
    this.app.stage.addChild(this.stateLabelLayer);
    this.app.stage.sortableChildren = true;
    // Await the directional-sprite manifest before the first draw (delta "Directional
    // sprite asset registry"): a loader rejection propagates out of `init()` as an
    // initialization failure rather than leaving the renderer to draw blank frames.
    await this.assets.load();
    if (signal?.aborted) {
      // Check C. The listener has already reclaimed by now — this is what stops a cancelled run
      // from going on to start a render loop nobody will ever cancel.
      reclaim();
      return;
    }
    this.buildGround();
    // Start the RAF loop (design D8): it redraws each frame off the injected clock, reading
    // the latest render-input so an independent lifecycle/season/epoch change lands within
    // one frame. Idempotent — `RenderLoop.init` schedules exactly one loop.
    this.loop.init((now) => this.onFrame(now));
  }

  /**
   * Supplies one accepted world frame + its render-input context (design D9). This no
   * longer draws directly — the RAF loop (started in `init`) drives all drawing off the
   * injected clock; `render` records the latest inputs and ingests the frame into the
   * per-entity seams exactly once (the facing TTL and the sample history are denominated in
   * accepted frames, never RAF frames). `tierChanged` (from the LOD controller's
   * `confirmKeyframe` returning `'tier-change'`) starts the retain-and-fade tier crossfade
   * (D9); the crossfade advances in wall-clock time on the RAF loop, so `_elapsedMs` (the
   * inter-accepted-frame delta) is unused here and kept only for the additive signature.
   *
   * `renderInput` is the additive, immutable render-input context (design D9): the streamed
   * `season`, the own-hive anchor + own-colony lifecycle, a replay `playhead` slot, and the
   * two monotonic discontinuity counters — fed in by the call site's store selectors, never
   * read from the store by the canvas. `wideColonyContext` colours wide-tier blobs/queens by
   * colony (design D-match-3); `ring` draws the gated bloom ring (design D-match-4). It
   * precedes `wideColonyContext`/`ring` so `CanvasRenderer` stays assignable to the
   * `InMatchRenderer` seam (which carries only the render-input).
   */
  render(
    world: WorldSnapshot,
    camera: Camera,
    _elapsedMs: number,
    tierChanged: boolean,
    renderInput: RenderInput = LIVE_PLAY_RENDER_INPUT,
    wideColonyContext?: WideColonyContext,
    ring: BloomRingGeometry | null = null,
  ): void {
    this.lastRenderInput = renderInput;
    this.latestWorld = world;
    this.latestCamera = camera;
    this.latestWide = wideColonyContext;
    this.latestRing = ring;

    if (this.crossfade === null) {
      this.crossfade = new CrossfadeManager<WorldSnapshot>(world, MOTION_BUDGET_MS);
    } else {
      this.crossfade.swap(world, tierChanged);
    }

    // Ingest this accepted frame into the per-entity seams exactly once. Close-tier bee
    // positions feed both the facing derivation (aged once per accepted close frame, D3) and
    // the interpolation buffer; a wide frame carries no close bees but still advances the
    // presentation clock's tier token so a close→wide→close transition resets the history.
    const now = this.clock.now();
    const tokens = {
      tier: world.tier,
      streamEpoch: renderInput.streamEpoch,
      playhead: renderInput.playhead,
    };
    if (world.tier === Tier.Close) {
      const bees = [...world.bees.values()];
      // The own-queen anchor is fed to the facing deriver as one extra sample under a reserved
      // id, so she gets a heading derived from her own movement exactly as a bee does. She is not
      // in `world.bees` (she is not a wire bee at all — the anchor carries her), so without this
      // the deriver never sees her and she falls back to frame 0, permanently facing north.
      //
      // A static anchor (live mode's hive point) never clears the min-speed threshold, so it holds
      // whatever it was seeded with for ever; only an anchor that actually moves — the demo's,
      // pinned to her simulated position — produces a turning queen. That is why `seededHeading`
      // seeds a non-positive id to **0** rather than scattering it: this reserved id is exactly the
      // case where the seed is permanent, and design D4's rest frame for the queen is 0. Scattering
      // it left live mode's queen facing down-left for the life of the match.
      const facingSamples: FacingSample[] = bees.map((b) => ({ id: b.id, position: b.position }));
      if (renderInput.ownHiveAnchor !== null) {
        facingSamples.push({ id: QUEEN_FACING_ID, position: renderInput.ownHiveAnchor });
      }
      this.facing.onCloseFrame(facingSamples, {
        connectionEpoch: renderInput.connectionEpoch,
        streamEpoch: renderInput.streamEpoch,
      });
      this.presentClock.ingest(
        bees.map((b) => ({ id: b.id, tick: world.tick, position: b.position })),
        world.tick,
        tokens,
        now,
      );
      // Dead-bee corpse lifecycle (design "Dead-bee corpse presentation", bee-visual-states
      // bug c): classify this accepted frame's disappearances (churn / viewport-exit /
      // death) against the SAME frame's projected screen positions + `truncated` cap
      // pressure. Viewport pixel size is read from the live `app.screen` here (the same
      // source `drawTier` already reads for `depthScale`) — never a new `render()`
      // parameter.
      const corpseSamples: CorpseFrameSample[] = bees.map((b) => {
        const screen = worldToScreen(camera, b.position);
        return {
          id: b.id,
          position: b.position,
          screenX: screen.x,
          screenY: screen.y,
          heading: this.facing.heading(b.id) ?? 0,
        };
      });
      this.corpses.onCloseFrame(
        corpseSamples,
        { truncated: world.truncated ?? false },
        { width: this.app.screen.width || 1, height: this.app.screen.height || 1 },
        // The WHOLE render-input, not a rebuilt `{ connectionEpoch }` literal (WAG-9). `CorpseEpochs`
        // is a structural subset of `RenderInput` precisely so this can be a pass-through: a literal
        // here would silently drop a field added to `CorpseEpochs` — as `playhead` was — and this
        // body is outside the unit-tested surface, so the compiler is the only thing that can catch
        // it. `renderer.test.ts` covers the extracted pure functions; nothing constructs a
        // `CanvasRenderer`.
        renderInput,
      );
      // Damage-flash classification (combat-strikes-and-damage-flash, design E4): one accepted
      // close frame's bee health / cell hp-bucket samples, fed the WHOLE discontinuity token
      // set (`tier`/`connectionEpoch`/`streamEpoch`/`playhead`) — `tier` is load-bearing here
      // and deliberately NOT part of `CorpseEpochs`' set above (a value-diff tracker, not a
      // disappearance heuristic; see `damage-flash.ts`'s own doc comment).
      this.flashes.onCloseFrame(
        world.tick,
        bees.map((b) => ({ id: b.id, health: b.health })),
        [...world.cells.values()].map((c) => ({ id: c.id, hpBucket: c.hpBucket })),
        {
          tier: world.tier,
          connectionEpoch: renderInput.connectionEpoch,
          streamEpoch: renderInput.streamEpoch,
          playhead: renderInput.playhead,
        },
      );
      // Dance watcher set (dance-visuals): derived once per accepted close frame (not per
      // RAF — Architecture review A2); the drawables reuse this cache each RAF. `world.dances`
      // (dancerId→record) and `world.bees` are structurally the `DanceWorld` shape.
      this.dances.onCloseFrame(
        { dances: world.dances, bees: world.bees, danceRadius: world.danceRadius },
        { connectionEpoch: renderInput.connectionEpoch },
      );
      // Flower attachment (flower-attach-meter review finding): the same once-per-accepted-
      // close-frame derivation as the trackers above, so the O(bees × patches) scan runs at
      // the streamed cadence rather than every RAF.
      this.beeAttachments = attachmentsForFrame(bees, world.patches.values());
    } else {
      this.presentClock.ingest([], world.tick, tokens, now);
      // Corpses, flashes and dances are close-tier-only concepts: clear tracked state on a
      // close→wide transition rather than let a stale position/id/health carry across the
      // tier change.
      this.corpses.clear();
      this.flashes.clear();
      this.dances.clear();
      // `beeAttachments` is deliberately NOT cleared here: during a close→wide crossfade the
      // fading-out OLD tier is still the last close snapshot (`buildDrawables(camera,
      // this.crossfade.old, ...)`), and it is handed this SAME cache — clearing it here would
      // strip attachment from every fading-out bee mid-crossfade. A wide-tier `buildDrawables`
      // call never reads `attachedTo` at all (its `world.tier === Tier.Close` gate), so the
      // stale map sits unread until the next close frame overwrites it wholesale.
    }
  }

  /**
   * Records an updated render-input context **without** ingesting a world frame
   * (implementation-review F13 / design D9). The render-input signals — streamed `season`,
   * the own-hive anchor, the own-colony lifecycle, and the discontinuity counters — arrive
   * independently of world deltas; when one changes with no accompanying world/sequence
   * update, the call site (`InMatchCanvas`/`Match`) pushes it here so the RAF loop, which
   * reads `lastRenderInput` every frame, reflects it within one frame — an elimination-only
   * lifecycle flip clears the anchor, a season change reconciles the ground — even while the
   * stream stalls. Crucially this does **not** touch the facing / sample-history seams (no
   * `onCloseFrame`/`ingest`): those are denominated in accepted world frames, so a bare
   * render-input update must not age the facing TTL as if another close frame had arrived.
   */
  updateRenderInput(renderInput: RenderInput): void {
    this.lastRenderInput = renderInput;
  }

  /**
   * One RAF frame (design D8): reads the latest inputs, selects the animation mode from the
   * degradation monitor, reconciles the season ground, advances the tier crossfade in
   * wall-clock time, and redraws the current (and any fading old) tier's drawables — the
   * anchor merged into the close-tier list. Runs even with no world yet so the ground shows.
   */
  private onFrame(now: number): void {
    const elapsedMs = this.lastFrameMs === null ? 0 : now - this.lastFrameMs;
    this.lastFrameMs = now;
    const world = this.latestWorld;
    const camera = this.latestCamera;
    const input = this.lastRenderInput;

    // Degradation → per-zoom animation mode (design D7/D9). Both trigger arms are live in
    // production: the streamed `ClosePayload.truncated`/`totalCount` cap pressure (plumbed
    // onto the `WorldSnapshot` at close-keyframe ingest, read via `degradationSignalFor` —
    // implementation-review F12), and the frame-rate guard (the on-device-meaningful signal).
    // A truncated close payload now proactively selects `reduced` after the hysteresis window,
    // dropping hover-bob + shadow exactly when the population is at the cap.
    const degraded = this.degradation.sample(degradationSignalFor(world));
    const tier = world ? world.tier : Tier.Wide;
    const animMode = mode(tier, degraded);

    // Season ground behind the draw list (design D5): pure model advanced each frame, then
    // the tiling sprites synced to its current/old tile + crossfade progress.
    this.ground.update(input.season, animMode, elapsedMs);
    this.syncGround();

    // Ages/expires held-or-fading corpses in wall-clock time (design "Dead-bee corpse
    // presentation"), independent of whether a new accepted close frame has arrived —
    // the same wall-clock-advance pattern as the tier crossfade below.
    this.corpses.advance(elapsedMs);
    // Ages/expires active damage flashes the same way (combat-strikes-and-damage-flash,
    // design E4) — a no-op for any flash classified under a playhead, which instead ages in
    // `onCloseFrame` against the playhead (a paused replay holds its flashes).
    this.flashes.advance(elapsedMs);

    if (world !== null && camera !== null && this.assetsReady) {
      this.syncScatter(camera);
      if (this.crossfade !== null) {
        this.crossfade.advance(elapsedMs);
      }
      const current = this.crossfade ? this.crossfade.current : world;
      const headingFor = (id: number): number | undefined => this.facing.heading(id);
      // Flower attachment (flower-attach-meter review finding): the RAF loop reuses the map
      // derived once per accepted close frame (`render`'s ingestion above) instead of
      // rescanning every bee against every patch on every animation frame.
      const attachedTo = (id: number): readonly [number, number] | undefined => this.beeAttachments.get(id);
      // Damage-flash lookup (combat-strikes-and-damage-flash, design E7/E8): a Map built once
      // per RAF frame from the tracker's current BEE flashes; the cell half is read separately in
      // `drawHiveCells`, because cells draw through their own `Graphics` path and have no
      // `WorldDrawable` (see `hive-cells.ts`). Otherwise the same "derive once per RAF,
      // read per drawable" pattern `headingFor`/`attachedTo` already use.
      const beeFlashIntensity = new Map<number, number>();
      for (const flash of this.flashes.flashes) {
        if (flash.kind === 'bee') beeFlashIntensity.set(flash.id, flash.intensity);
      }
      const flashFor = (id: number): number | undefined => beeFlashIntensity.get(id);

      // The own-hive + own-queen anchor (design D4) and any live corpses enter the same
      // y-sorted list, on the close tier only (the colony screen; the whole-field match
      // screen — and a zoomed-out colony screen, colony-match-nav — is wide). The anchor
      // clears itself when null/eliminated; corpses clear themselves once expired
      // (`CorpseTracker.advance`); hive-egg-cells' live-cells suppression of the static
      // placeholder tile — all pulled into the pure, tested `tierDrawables` (D-086).
      const currentList = tierDrawables(
        camera,
        current,
        this.latestWide,
        headingFor,
        input,
        this.corpses.corpses,
        attachedTo,
        flashFor,
      );
      // Symbol mode (`symbols.ts`): below the threshold, bees and flower patches are drawn as
      // colour dots by `drawSymbols` instead of as sprites, so they are withheld from the
      // sprite list entirely — no node is created for them, which is also what keeps the
      // scene graph small at exactly the zoom that puts the most entities on screen.
      const symbols = symbolModeForScale(camera.scale);
      const spriteList = symbols
        ? currentList.filter((d) => !drawsAsSymbol(d.kind))
        : currentList;
      this.drawTier(this.sprites, spriteList, camera, 1, animMode, now);
      // The bee bars read the SAME list `drawTier` just drew, so a bar can never be drawn for
      // a bee the frame did not draw — including a bee that drew as a symbol. Only the current
      // tier gets bars — a fading-out old tier is decoration, not a readout.
      this.drawBeeBars(camera, spriteList, now);
      // …and the labels read that same list again, one layer higher (WAG-36). Only the current
      // tier: a fading-out old tier is decoration, not a readout.
      this.drawStateLabels(camera, current, spriteList, now);
      this.drawSymbols(camera, symbols ? currentList : EMPTY_DRAWABLES);

      if (this.crossfade !== null && this.crossfade.isCrossfading() && this.crossfade.old !== null) {
        const oldList = buildDrawables(
          camera,
          this.crossfade.old,
          this.latestWide,
          headingFor,
          input.ownColonyId,
          attachedTo,
          flashFor,
        );
        this.drawTier(this.oldSprites, oldList, camera, 1 - this.crossfade.progress, animMode, now);
      } else {
        this.clearLayer(this.oldSprites);
      }
      this.drawRing(camera, this.latestRing);
      this.drawPlayBoundary(camera);
      this.drawHiveCells(camera, world, animMode, now);
      this.drawPatchMeters(camera, world, animMode);
      this.drawDanceOverlay(camera, world, animMode, now);
    }
    this.app.render();
  }

  /** Builds the two ground fill layers once the manifest has loaded (called from `init`).
   * Both are plain `Graphics` rects covering the canvas (design "flat season-tinted fill",
   * field-ground-scatter — replacing the old tiled `TilingSprite` ground, which never moved
   * with the camera and read as an obvious repeating texture); the old one is hidden until
   * a season crossfade. */
  private buildGround(): void {
    this.groundCurrent = new Graphics();
    this.groundCurrent.zIndex = 0;
    this.groundOld = new Graphics();
    this.groundOld.zIndex = 1; // the outgoing fill fades on top of the incoming one
    this.groundOld.visible = false;
    this.groundView.addChild(this.groundCurrent);
    this.groundView.addChild(this.groundOld);
    this.syncGround();
  }

  private groundGrainTexture(tile: AssetKey): Texture | null {
    const cached = this.groundGrainTextures.get(tile);
    if (cached !== undefined) return cached;
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = GROUND_GRAIN_TILE_SIZE;
    canvas.height = GROUND_GRAIN_TILE_SIZE;
    const context = canvas.getContext('2d');
    if (context === null) return null;
    const image = context.createImageData(GROUND_GRAIN_TILE_SIZE, GROUND_GRAIN_TILE_SIZE);
    image.data.set(groundGrainTile(groundFillColorForTile(tile)));
    context.putImageData(image, 0, 0);
    const texture = Texture.from(canvas);
    texture.source.scaleMode = 'nearest';
    this.groundGrainTextures.set(tile, texture);
    return texture;
  }

  /** Reconciles the two flat ground fills to the pure `GroundLayer` model each frame: the
   * incoming season's fill colour ({@link groundFillColorForTile}) at full viewport size,
   * and — during a `full`-mode season crossfade — the outgoing colour faded by
   * `1 − progress`, dropped (hidden) once the model releases it (design D5). The fill is
   * screen-space (it does not pan with the camera, unlike `scatterLayer`) — it is a flat
   * backdrop colour, not a textured surface with seams that would betray staying still. */
  private syncGround(): void {
    if (this.groundCurrent === null || this.groundOld === null) {
      return;
    }
    const width = this.app.screen.width || 1;
    const height = this.app.screen.height || 1;
    this.groundCurrent
      .clear()
      .rect(0, 0, width, height)
      .fill(groundFillColorForTile(this.ground.current));
    const currentTexture = this.groundGrainTexture(this.ground.current);
    if (currentTexture !== null) {
      this.groundGrainCurrent ??= new TilingSprite({ texture: currentTexture, width, height });
      if (this.groundGrainCurrent.parent === null) {
        this.groundGrainCurrent.zIndex = 0.5;
        this.groundView.addChild(this.groundGrainCurrent);
      }
      this.groundGrainCurrent.texture = currentTexture;
      this.groundGrainCurrent.width = width;
      this.groundGrainCurrent.height = height;
      const transform = groundGrainTransform(this.latestCamera ?? { offsetX: 0, offsetY: 0, scale: 1 });
      this.groundGrainCurrent.tilePosition.set(transform.tilePositionX, transform.tilePositionY);
      this.groundGrainCurrent.tileScale.set(transform.tileScaleX, transform.tileScaleY);
    }
    const old = this.ground.old;
    if (old !== null) {
      this.groundOld.visible = true;
      this.groundOld.clear().rect(0, 0, width, height).fill(groundFillColorForTile(old));
      this.groundOld.alpha = 1 - this.ground.progress;
      const oldTexture = this.groundGrainTexture(old);
      if (oldTexture !== null) {
        this.groundGrainOld ??= new TilingSprite({ texture: oldTexture, width, height });
        if (this.groundGrainOld.parent === null) {
          this.groundGrainOld.zIndex = 1.5;
          this.groundView.addChild(this.groundGrainOld);
        }
        this.groundGrainOld.texture = oldTexture;
        this.groundGrainOld.width = width;
        this.groundGrainOld.height = height;
        this.groundGrainOld.alpha = 1 - this.ground.progress;
        this.groundGrainOld.visible = true;
        const transform = groundGrainTransform(this.latestCamera ?? { offsetX: 0, offsetY: 0, scale: 1 });
        this.groundGrainOld.tilePosition.set(transform.tilePositionX, transform.tilePositionY);
        this.groundGrainOld.tileScale.set(transform.tileScaleX, transform.tileScaleY);
      }
    } else {
      this.groundOld.visible = false;
      if (this.groundGrainOld !== null) this.groundGrainOld.visible = false;
    }
  }

  /**
   * Realises the deterministic world-space ground scatter (grass tufts + rocks,
   * field-ground-scatter) for the current viewport: derives the visible world rect from the
   * camera and the live viewport pixel size by unprojecting two opposite screen corners
   * through {@link screenToWorld}, queries {@link scatterInViewport} for the (small,
   * cell-bucketed) set of items that fall within it, and reconciles one persistent `Sprite`
   * per item — create-on-first-sight / destroy-on-scroll-out, keyed by the item's stable
   * `key` (never by the viewport), so a sprite already on screen survives a small pan/zoom
   * rather than being torn down and recreated. Each sprite projects through {@link
   * worldToScreen} exactly like a bee (`buildDrawables`), inheriting the same tilt/squash
   * and {@link depthScale}, so scatter pans and zooms with the camera — the fix for
   * "the background doesn't move with drag".
   */
  private syncScatter(camera: Camera): void {
    const width = this.app.screen.width || 1;
    const height = this.app.screen.height || 1;
    const topLeft = screenToWorld(camera, { x: 0, y: 0 });
    const bottomRight = screenToWorld(camera, { x: width, y: height });
    const viewport: WorldRect = {
      minX: Math.min(topLeft[0], bottomRight[0]),
      maxX: Math.max(topLeft[0], bottomRight[0]),
      minY: Math.min(topLeft[1], bottomRight[1]),
      maxY: Math.max(topLeft[1], bottomRight[1]),
    };
    // Zoom level-of-detail: hold the on-screen landmark count roughly stable as the camera
    // pulls back, instead of carpeting a wide view in sub-pixel speckle.
    const detail = scatterDetailForScale(camera.scale);
    const seen = new Set<string>();
    for (const item of scatterInViewport(
      DEFAULT_SCATTER_SEED,
      viewport,
      detail,
      this.scatterHalf(),
    )) {
      seen.add(item.key);
      let sprite = this.scatterSprites.get(item.key);
      if (sprite === undefined) {
        sprite = new Sprite(this.assets.texture(item.sheet));
        sprite.anchor.set(0.5, 1); // feet at the node origin, matching every other sprite body
        this.scatterLayer.addChild(sprite);
        this.scatterSprites.set(item.key, sprite);
      }
      const screen = worldToScreen(camera, item.position);
      sprite.x = Math.round(screen.x);
      sprite.y = Math.round(screen.y);
      // Scatter is world-anchored like every other sprite, at its own larger footprint —
      // grass and rocks are ground texture, not entities (`SCATTER_SIZE_MULTIPLIER`).
      sprite.scale.set(
        depthScale(screen.y, height) * spriteCameraScale(camera.scale, SCATTER_SIZE_MULTIPLIER),
      );
    }
    for (const [key, sprite] of this.scatterSprites) {
      if (!seen.has(key)) {
        sprite.destroy();
        this.scatterSprites.delete(key);
      }
    }
  }

  /** Draws (or clears) the gated bloom ring (design D-match-4): an outline ellipse
   * at `ring.radius` about `ring.centre`, projected through the same tilt/squash as
   * every other world-space entity. `ring === null` (the production default, no
   * authoritative centre) clears it — never draws a ring about a guessed centre. */
  private drawRing(camera: Camera, ring: BloomRingGeometry | null): void {
    this.ring.clear();
    if (ring === null) {
      return;
    }
    const screen = worldToScreen(camera, ring.centre);
    const screenRx = ring.radius * camera.scale;
    const screenRy = ring.radius * camera.scale * Y_SQUASH;
    this.ring
      .ellipse(screen.x, screen.y, screenRx, screenRy)
      .stroke({ width: 1.5, color: 0xfffdf6, alpha: 0.8 });
  }

  /** The half-extent of the square the sim's movement clamp stops bees at, from the manifest
   * context this renderer was last handed. Falls back to the seed field when no manifest has
   * landed yet, which is the same constant every other pre-manifest default uses. */
  private boundaryHalf(): number {
    return playBoundaryHalf(
      this.latestWide?.fieldSize ?? 4096,
      this.latestWide?.playMarginU,
    );
  }

  /** The half-extent covered by grass tufts and rocks: the complete playable square. */
  private scatterHalf(): number {
    return scatterHalfForBoundary(this.boundaryHalf());
  }

  /** Draws the **play boundary** (`deathmatch-world` D-6): a dashed world-space rectangle at
   * `±(fieldSize / 2 + playMarginU)` — the wall bees actually stop at. Before this, a bee that
   * had walked to the clamp simply stopped in empty grass and the player had no way to know
   * why. Dashed rather than solid so it reads as a rule of the world, not as terrain, and
   * projected through the same tilt/squash as everything else so it sits on the ground plane.
   * A self-contained primitive paint like {@link drawRing} — GPU-real, device-exercised only;
   * the geometry it is drawn at is unit-tested in `play-boundary.test.ts`. */
  private drawPlayBoundary(camera: Camera): void {
    this.playBoundary.clear();
    const half = this.boundaryHalf();
    // The four corners in world space, projected — a squashed world square is still a screen
    // parallelogram, so the outline is stroked corner to corner rather than as a screen rect.
    const corners = ([
      [-half, -half],
      [half, -half],
      [half, half],
      [-half, half],
    ] as const).map((point) => worldToScreen(camera, point));
    const DASH = 12;
    const GAP = 9;
    for (let i = 0; i < corners.length; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % corners.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy);
      if (length < 1) continue;
      const ux = dx / length;
      const uy = dy / length;
      for (let t = 0; t < length; t += DASH + GAP) {
        const end = Math.min(t + DASH, length);
        this.playBoundary
          .moveTo(a.x + ux * t, a.y + uy * t)
          .lineTo(a.x + ux * end, a.y + uy * end);
      }
    }
    this.playBoundary.stroke({ width: 2, color: 0xfffdf6, alpha: 0.45 });
  }

  /**
   * Draws (or clears) the dance-visuals overlay (dance-visuals; UI.md §8) into a single
   * `Graphics` layer each RAF, from `DanceTracker.drawables` — the loud dancing indicator over
   * each dancer, the fixed-length tag-hued direction arrow toward the (dequantized) target,
   * and (in `full` mode) the quiet watcher pip over each bee within the dance radius. The
   * watcher set is cached at the accepted close frame (A2); this only projects + paints it.
   * Bee origins are the `PresentationClock`-interpolated positions ({@link
   * interpolatedDanceBees}, D-132), the same positions `drawTier` draws the bee sprites at
   * — not the raw confirmed `WorldSnapshot` tick positions — so the arrow/glyph/pips never
   * lag the body. A self-contained primitive paint like {@link drawRing} — GPU-real,
   * device-exercised only (D-086). Non-close / `none` mode clears the layer.
   */
  private drawDanceOverlay(
    camera: Camera,
    world: WorldSnapshot,
    animMode: AnimationMode,
    now: number,
  ): void {
    this.danceOverlay.clear();
    if (world.tier !== Tier.Close) {
      return;
    }
    const danceMode: DanceRenderMode = animMode; // full/reduced/none share the same tokens
    const interpolatedBees = interpolatedDanceBees(world.bees, (id) => this.presentClock.positionAt(id, now));
    const drawables = this.dances.drawables(
      { dances: world.dances, bees: interpolatedBees, danceRadius: world.danceRadius },
      danceMode,
    );
    const HONEY = 0xe8a72c;
    const HONEY_DEEP = 0xc9861a;
    const HONEY_PALE = 0xf6e2b0;
    for (const d of drawables) {
      if (d.kind === 'dance-arrow') {
        const so = worldToScreen(camera, d.origin);
        // Squash-correct screen direction: project a unit world step along the dance angle.
        const step = worldToScreen(camera, [
          d.origin[0] + Math.cos(d.angle),
          d.origin[1] + Math.sin(d.angle),
        ]);
        let sx = step.x - so.x;
        let sy = step.y - so.y;
        const len = Math.hypot(sx, sy);
        if (len < 1e-3) continue;
        sx /= len;
        sy /= len;
        const ex = so.x + sx * ARROW_LENGTH;
        const ey = so.y + sy * ARROW_LENGTH;
        const hue = danceTagColor(d.tagHash);
        this.danceOverlay.moveTo(so.x, so.y).lineTo(ex, ey).stroke({ width: 4, color: hue, alpha: 0.95 });
        // A chunky chevron head.
        const back = 8;
        const perp = 6;
        this.danceOverlay
          .moveTo(ex, ey)
          .lineTo(ex - sx * back - sy * perp, ey - sy * back + sx * perp)
          .moveTo(ex, ey)
          .lineTo(ex - sx * back + sy * perp, ey - sy * back - sx * perp)
          .stroke({ width: 4, color: hue, alpha: 0.95 });
      } else if (d.kind === 'dance-indicator') {
        const s = worldToScreen(camera, d.origin);
        // A loud honey glyph above the dancer: a pulsing ring (full) + a bobbing dot.
        const pulse = d.animated ? 4 + 3 * (0.5 + 0.5 * Math.sin(now / 150)) : 6;
        this.danceOverlay
          .circle(s.x, s.y - 18, pulse)
          .stroke({ width: 2.5, color: HONEY, alpha: 0.9 });
        this.danceOverlay.circle(s.x, s.y - 18, 2.4).fill({ color: HONEY_DEEP });
      } else {
        // A quiet watcher pip.
        const s = worldToScreen(camera, d.position);
        this.danceOverlay
          .circle(s.x, s.y - 13, 3)
          .fill({ color: HONEY_PALE })
          .stroke({ width: 1.5, color: HONEY_DEEP, alpha: 0.85 });
      }
    }
  }

  /**
   * Draws (or clears) the per-bee health/nectar bars (bee-body-palette, playtest finding 10)
   * into ONE batched `Graphics` per frame — never a `Graphics` per bee, since the endgame is
   * thousands of close-tier bees.
   *
   * Which bars a bee shows, how full each is, and where it sits are the pure {@link beeBars}
   * seam's concern (a full health bar and an empty nectar bar are suppressed as
   * uninformative, so a healthy field stays quiet); this only projects and paints. Bars are
   * dropped wholesale below {@link beeBarsVisibleAtScale}, where bees project closer together
   * than one bar is wide and the bars would smear — the same zoom-legibility cut
   * `scatterDetailForScale` makes for the ground scatter.
   *
   * Each bar follows its bee's `PresentationClock`-INTERPOLATED position (the same position
   * `drawTier` draws the body at, and the same substitution `interpolatedDanceBees` makes for
   * the dance overlay), so a bar never lags the body it belongs to. It does NOT follow the
   * decorative hover-bob: that is a ±1.5 px oscillation under a bar sitting 32 px up, and
   * tracking it would double the per-bee `hoverBob` cost in exactly the frame budget this
   * batching exists to protect. A self-contained primitive paint like {@link drawRing} —
   * GPU-real, device-exercised only (D-086).
   */
  private drawBeeBars(camera: Camera, drawables: readonly WorldDrawable[], now: number): void {
    this.beeBarOverlay.clear();
    if (!beeBarsVisibleAtScale(camera.scale)) {
      return;
    }
    const viewportHeight = this.app.screen.height || 1;
    for (const drawable of drawables) {
      // A corpse carries no condition (`health`/`nectar` unset), so it draws no bars.
      if (drawable.kind !== 'bee' || drawable.health === undefined || drawable.nectar === undefined) {
        continue;
      }
      const bars = beeBars(drawable.health, drawable.nectar);
      if (bars.length === 0) {
        continue;
      }
      const { x: screenX, y: screenY } = beeDrawScreenPosition(
        drawable,
        camera,
        (id, t) => this.presentClock.positionAt(id, t),
        now,
      );
      // The bars ride the BEE's scale, camera included, so a bar can never outgrow the bee
      // it belongs to as the camera pulls back.
      const scale = depthScale(screenY, viewportHeight) * spriteCameraScale(camera.scale);
      const width = BEE_BAR_WIDTH_PX * scale;
      const height = BEE_BAR_HEIGHT_PX * scale;
      const x = Math.round(screenX) - width / 2;
      for (const bar of bars) {
        const y = Math.round(screenY) + bar.offsetY * scale;
        this.beeBarOverlay
          .rect(x, y, width, height)
          .fill({ color: BEE_BAR_TRACK_COLOR, alpha: BEE_BAR_TRACK_ALPHA });
        if (bar.fill > 0) {
          this.beeBarOverlay.rect(x, y, width * bar.fill, height).fill({
            // Health ramps green → amber → red with the fill; nectar is always the honey colour.
            color: bar.kind === 'health' ? healthBarColor(bar.fill) : BEE_BAR_NECTAR_COLOR,
          });
        }
        // The health bar's white outline, drawn LAST so it sits over both the track and the fill —
        // it bounds the whole bar, not just the filled part, and is what separates health from the
        // honey nectar bar beneath it once health has gone amber.
        if (bar.kind === 'health') {
          this.beeBarOverlay.rect(x, y, width, height).stroke({
            width: BEE_BAR_HEALTH_OUTLINE_WIDTH_PX * scale,
            color: BEE_BAR_HEALTH_OUTLINE_COLOR,
            // **Outside the rect, not inside.** `alignment: 0` was chosen so the outline could not
            // overlap the bar above or below — but inside a `BEE_BAR_HEIGHT_PX` of 3 a 1 px stroke
            // eats the top and bottom rows and both side columns, leaving a single interior pixel of
            // fill. That spent the whole green/amber/red ramp the bar height was raised for in order
            // to gain a border. Outside costs nothing: there is no bar above the health bar, and
            // `BEE_BAR_GAP_PX` is exactly 1, so the bottom edge of this stroke lands flush against
            // the nectar bar's top edge without overlapping it — the outline *becomes* the divider
            // between the two, which is the second job it was added to do.
            alignment: 1,
          });
        }
      }
    }
  }

  /**
   * Paints the in-world state labels (WAG-36, `bee-state-flash`) — the canvas's first text.
   *
   * Every decision is the pure {@link StateLabelLayer}: which labels are live, their alpha off
   * the 120/640/240 curve, where each sits below its bee's ground origin, who survives a crowd,
   * and what a pick means. This body only reconciles the result onto a pooled `Sprite` per drawn
   * label, over a texture cached per distinct **string** — so the cost is per state name in play,
   * never per bee, and the pool is reused frame to frame rather than retained per entity.
   *
   * Positions come from {@link stateLabelBees}, which resolves each bee through the one shared
   * {@link beeDrawScreenPosition} rule the body and the bars already share.
   *
   * The whole layer is inert on the live path: `beeStateNames` is a rounds-only channel and is
   * **absent** there (design **E5**), which clears the layer and returns before the pure layer is
   * even consulted — so a live frame costs nothing and grows no dead branch.
   *
   * A self-contained primitive paint like {@link drawBeeBars} — GPU-real, device-exercised only
   * (D-086).
   */
  private drawStateLabels(
    camera: Camera,
    world: WorldSnapshot,
    drawables: readonly WorldDrawable[],
    now: number,
  ): void {
    const names = this.lastRenderInput.beeStateNames;
    if (names === undefined) {
      this.hideStateLabelSprites(0);
      return;
    }
    const playback = this.stateLabelPlayback;
    if (playback.seek === true) {
      // A seek is an event, not a mode: consumed by this frame's re-baseline and then cleared,
      // so a caller that sets it once cannot leave the layer silently re-baselining forever.
      this.stateLabelPlayback = { ...playback, seek: false };
    }
    const result = this.stateLabels.labels.frame({
      nowMs: now,
      bees: stateLabelBees(
        camera,
        drawables,
        names,
        (id) => world.bees.get(id)?.colonyId,
        (id, t) => this.presentClock.positionAt(id, t),
        now,
      ),
      cameraScale: camera.scale,
      viewportWidth: this.app.screen.width || 1,
      viewportHeight: this.app.screen.height || 1,
      subjectColonyId: playback.subjectColonyId,
      scrubbing: playback.scrubbing,
      seek: playback.seek,
      pickedBeeAppearsLater: playback.pickedBeeAppearsLater,
    });

    result.labels.forEach((label, index) => {
      let sprite = this.stateLabelSprites[index];
      if (sprite === undefined) {
        sprite = new Sprite();
        sprite.anchor.set(0.5, 0);
        this.stateLabelSprites[index] = sprite;
        this.stateLabelLayer.addChild(sprite);
      }
      const texture = this.stateLabels.texture(label.name);
      sprite.texture = texture;
      sprite.visible = true;
      sprite.alpha = label.alpha;
      // The raster is authored at the supersample and carries the halo's own padding, so it is
      // both larger than the glyph box and drawn back down by the same factor — the label's
      // on-screen size stays `STATE_LABEL_FONT_PX × spriteCameraScale × depthScale`.
      const shrink = label.scale / STATE_LABEL_TEXTURE_SUPERSAMPLE;
      sprite.scale.set(shrink);
      sprite.x = Math.round(label.screenX);
      sprite.y =
        label.screenY - ((texture.height / STATE_LABEL_TEXTURE_SUPERSAMPLE - STATE_LABEL_FONT_PX) / 2) * label.scale;
    });
    this.hideStateLabelSprites(result.labels.length);
  }

  /** Hides every pooled label sprite from `from` on — the pool is reused, never torn down, so
   * a busy frame's sprites are already allocated for the next one. */
  private hideStateLabelSprites(from: number): void {
    for (let i = from; i < this.stateLabelSprites.length; i += 1) {
      this.stateLabelSprites[i]!.visible = false;
    }
  }

  /**
   * Rasterises one state name into a texture: mono ink with a paper halo (design **E12**), from
   * the kit's existing `--font-mono` family at the authored {@link STATE_LABEL_FONT_PX} base —
   * no plate, no rectangle on the field, no bitmap-font asset. Called once per distinct name
   * actually drawn (see {@link StateLabelPainter}), never per bee and never per frame.
   *
   * The glyph box is laid out with {@link STATE_LABEL_CHAR_WIDTH_PX} and the halo padded with
   * {@link STATE_LABEL_HALO_PAD_PX} — the same two constants the layer measures its collision
   * rectangle with, so the crowd bound describes the label actually painted, halo included.
   *
   * Returns the shared empty texture with no DOM (never a crash, never a blank sprite claiming
   * ownership of a shared texture) — the `recolorSheet` precedent.
   */
  private createLabelTexture(name: string): Texture {
    const s = STATE_LABEL_TEXTURE_SUPERSAMPLE;
    const px = STATE_LABEL_FONT_PX * s;
    const pad = STATE_LABEL_HALO_PAD_PX * s;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(name.length * STATE_LABEL_CHAR_WIDTH_PX * s + pad * 2);
      canvas.height = Math.ceil(px * 1.4);
      const ctx = canvas.getContext('2d');
      if (ctx === null) {
        return Texture.EMPTY;
      }
      ctx.font = `600 ${px}px "IBM Plex Mono", "Menlo", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const x = canvas.width / 2;
      const y = (canvas.height - px) / 2;
      ctx.lineWidth = Math.max(1, STATE_LABEL_FONT_PX * 0.3) * s;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = STATE_LABEL_HALO;
      ctx.strokeText(name, x, y);
      ctx.fillStyle = STATE_LABEL_INK;
      ctx.fillText(name, x, y);
      // `skipCache` — this texture belongs to THIS renderer and is destroyed with it.
      return Texture.from(canvas, true);
    } catch {
      return Texture.EMPTY;
    }
  }

  /** The rounds screens' playback signals for the label layer (subject colony, scrub, seek,
   * the trace's appears-later lookup). Never called on the live path. */
  setStateLabelPlayback(playback: StateLabelPlayback): void {
    this.stateLabelPlayback = playback;
  }

  /** Picks (or clears) the bee whose state is labelled continuously and never suppressed. */
  setPickedBee(beeId: number | null): void {
    this.stateLabels.labels.setPickedBee(beeId);
  }

  /** The picked bee, or `null` — read back after a frame, since a pick whose bee has left the
   * trace clears itself (design **E9**). */
  get pickedBeeId(): number | null {
    return this.stateLabels.labels.pickedBeeId;
  }

  /** Releases every cached label texture and drops the layer's baselines and pick — on **trace
   * load and version switch**, where neither the bee ids nor the state names carry over
   * (design **E8**). */
  invalidateStateLabels(): void {
    this.stateLabels.invalidate();
    this.hideStateLabelSprites(0);
  }

  /**
   * Draws (or clears) the cell-by-cell hive (hive-egg-cells; UI.md §8) into a single `Graphics`
   * layer each RAF, from the pure {@link hiveCellDrawables} seam over the close-tier wire cells.
   * One squashed hex per cell — ruin (decaying wax) > damaged > intact fill — plus an additive
   * bright egg/brood dot over each OCCUPIED cell, so egg-vs-empty reads at a glance regardless of
   * health. Cells are static geometry: `full`/`reduced` paint identically; `none` (wide) clears.
   * A self-contained primitive paint like {@link drawDanceOverlay} — GPU device-exercised only
   * (D-086). The hex radius/colours are provisional and tuned on device.
   */
  /**
   * Paints **symbol mode** (`symbols.ts`): one colour dot per bee and per flower patch, in a
   * single batched `Graphics`, standing in for the sprites below the symbol zoom threshold.
   *
   * The colours are the same information the sprites carry, not a second scheme: a bee takes
   * its `beeTintFor` body colour (so own-type and enemy-colony read identically in both
   * modes) with an enemy's band colour as a ring, and a patch takes its variety's colour, or
   * the drained colour once its pool is gone. A caller that is not in symbol mode passes an
   * empty list, which clears the layer.
   *
   * A self-contained primitive paint like {@link drawHiveCells} — GPU device-exercised only
   * (D-086); the colour/geometry decisions are the pure, unit-tested `symbols.ts` seam.
   */
  private drawSymbols(camera: Camera, drawables: readonly WorldDrawable[]): void {
    this.symbolOverlay.clear();
    if (drawables.length === 0) {
      return;
    }
    const beeRadius = beeSymbolRadius(camera.scale);
    const patchRadius = patchSymbolRadius(camera.scale);
    for (const d of drawables) {
      if (d.kind === 'patch') {
        this.symbolOverlay
          .circle(d.screenX, d.screenY, patchRadius)
          .fill({ color: flowerSymbolColor(d.sheet as AssetKey | undefined), alpha: 0.95 });
      } else if (d.kind === 'bee') {
        const tint = d.tint;
        this.symbolOverlay
          .circle(d.screenX, d.screenY, beeRadius)
          .fill({ color: tint?.body ?? 0xffffff, alpha: d.dead === true ? 0.4 : 0.95 });
        // An enemy's band colour becomes a ring, so "not mine" survives the switch to
        // symbols — it is the one thing about a bee a player must not have to zoom to learn.
        if (tint?.band != null) {
          this.symbolOverlay
            .circle(d.screenX, d.screenY, beeRadius)
            .stroke({ width: 1, color: tint.band, alpha: 0.9 });
        }
      }
    }
  }

  private drawHiveCells(
    camera: Camera,
    world: WorldSnapshot,
    animMode: AnimationMode,
    nowMs: number,
  ): void {
    this.hiveCellOverlay.clear();
    if (world.tier !== Tier.Close) {
      this.hideHiveCellSprites(0);
      return;
    }
    const mode: HiveCellRenderMode = animMode; // full/reduced paint identically; none = wide
    // The tracker's CELL flashes (combat-strikes-and-damage-flash 10.6). The cell hp bucket was
    // put on the wire at the cost of a protocol-version bump for exactly this, so the tracker's
    // cell entries must reach a paint rather than being computed and discarded — cells draw
    // through this `Graphics` path, never through `WorldDrawable`, so the bee-side
    // `flashIntensity` wiring in `renderFrame` does not reach them.
    const cellFlashIntensity = new Map<number, number>();
    for (const flash of this.flashes.flashes) {
      if (flash.kind === 'cell') cellFlashIntensity.set(flash.id, flash.intensity);
    }
    const drawables = hiveCellDrawables({ cells: world.cells }, mode, (id) =>
      cellFlashIntensity.get(id),
    );
    this.freshCells.observe(world.cells.keys(), nowMs);
    if (drawables.length === 0) {
      this.hideHiveCellSprites(0);
      return;
    }
    // The egg is baked into the tile's own sheet frame (it sits IN the bore, lit by the same
    // key as the wax), so the seam's separate `hive-egg` drawables select a frame here rather
    // than painting a dot of their own. Collected first because a cell's own fill drawable
    // comes before its egg marker.
    const brooding = new Set<number>();
    for (const d of drawables) {
      if (d.kind === 'hive-egg') brooding.add(d.id);
    }
    // Back to front: these tiles have height, so a nearer cell's rim must occlude the one
    // behind it. World y is the depth key, exactly as for every other world entity.
    const cells = drawables
      .filter((d): d is Extract<typeof d, { kind: 'hive-cell' }> => d.kind === 'hive-cell')
      .sort((a, b) => a.position[1] - b.position[1]);
    // Sprite scale is the plain camera scale with no per-family multiplier: the sheet is
    // authored at 12 px per `HIVE_CELL_WORLD_RADIUS`, which is exactly 1:1 at
    // `SPRITE_NATIVE_ZOOM`, so a tile lands on the same footprint the flat hex used to.
    // Deliberately NOT multiplied by `depthScale` — that is a per-entity depth cue, and
    // applying it to ground structure would make the comb stop tiling.
    const scale = spriteCameraScale(camera.scale);
    // The flash still strokes the cell's own hex outline, over whichever tile it drew.
    const radius = Math.max(1, HIVE_CELL_WORLD_RADIUS * camera.scale);
    cells.forEach((d, i) => {
      const s = worldToScreen(camera, d.position);
      const sprite = this.hiveCellSprite(i);
      sprite.texture = this.frameTexture(
        'hiveCells',
        hiveCellFrame(d.fill, brooding.has(d.id), this.freshCells.fresh(d.id, nowMs)),
        undefined,
        HIVE_CELL_FRAME_SIZE,
      );
      sprite.visible = true;
      sprite.x = Math.round(s.x);
      sprite.y = Math.round(s.y);
      sprite.zIndex = i;
      sprite.scale.set(scale);
      // The damage flash, composed OVER the tile on the cell's own geometry: the same red/white
      // ramp a struck bee gets (`flashPaintAt`), stroked on the hex's own edge so the cell's
      // tile — and therefore its intact/damaged/ruin state — survives it.
      //
      // A cell keeps the STROKE treatment where a bee moved to a whole-body overlay: the 2026-08-11
      // complaint was a red ring floating around a bee, and this stroke is not that — it is the
      // cell's own outline, which is the shape the thing being damaged actually has.
      const flash = flashPaintAt(d.flashIntensity);
      if (flash !== null) {
        this.hiveCellOverlay.poly(hexPoints(s.x, s.y, radius)).stroke({
          width: HIVE_CELL_FLASH_STROKE_WIDTH,
          color: flash.overlayColor,
          alpha: flash.overlayAlpha,
        });
      }
    });
    this.hideHiveCellSprites(cells.length);
  }

  /** The pooled comb sprite at `index`, grown on demand. Pooled, never torn down per frame:
   * the endgame comb is up to `CELL_CAP_DEFAULT` tiles and they are all the same texture
   * source, so reuse keeps this one batch. Mirrors `hideStateLabelSprites`' pool. */
  private hiveCellSprite(index: number): Sprite {
    let sprite = this.hiveCellSprites[index];
    if (sprite === undefined) {
      sprite = new Sprite();
      sprite.anchor.set(0.5, 0.5); // the sheet centres each tile's TOP FACE in its frame
      this.hiveCellLayer.addChild(sprite);
      this.hiveCellSprites[index] = sprite;
    }
    return sprite;
  }

  /** Hides every pooled comb sprite from `from` on — the pool survives for the next frame. */
  private hideHiveCellSprites(from: number): void {
    for (let i = from; i < this.hiveCellSprites.length; i++) {
      this.hiveCellSprites[i].visible = false;
    }
  }

  /**
   * Draws (or clears) the patch nectar meter (flower-attach-meter, finding 11; UI.md §8) into
   * a single `Graphics` layer each RAF, from the pure {@link patchMeterDrawables} seam: a bar
   * over every non-full patch, hidden entirely once the camera scale would render it
   * sub-legible ({@link meterLegibleAtScale}) — the same zoom-suppression idea as
   * `ground-scatter.ts`'s scatter thinning. A self-contained primitive paint like
   * {@link drawDanceOverlay}/{@link drawHiveCells} — GPU device-exercised only (D-086). Static
   * geometry (no animation): `full`/`reduced` paint identically (mirrors `drawHiveCells`);
   * `none` (wide tier) clears — the wide tier draws its own aggregate `bloom` marker instead
   * (`buildDrawables`).
   */
  private drawPatchMeters(camera: Camera, world: WorldSnapshot, animMode: AnimationMode): void {
    this.patchMeterOverlay.clear();
    if (world.tier !== Tier.Close) {
      return;
    }
    const mode: PatchMeterRenderMode = animMode; // full/reduced paint identically; none = wide
    const bars = patchMeterDrawables(world.patches.values(), camera.scale, mode);
    if (bars.length === 0) {
      return;
    }
    const TRACK = 0x2a2013;
    const FILL_LOW = 0xb5791a; // depleting: a deeper honey, echoing the hive-cell "damaged" fill
    const FILL_HIGH = 0xe8a72c; // still plentiful: the same live honey as an intact cell
    const width = METER_BAR_WIDTH_PX * camera.scale;
    const height = METER_BAR_HEIGHT_PX * camera.scale;
    for (const bar of bars) {
      const s = worldToScreen(camera, bar.position);
      const x = s.x - width / 2;
      const y = s.y - METER_BAR_RISE_PX * camera.scale;
      this.patchMeterOverlay.rect(x, y, width, height).fill({ color: TRACK, alpha: 0.75 });
      if (bar.fraction > 0) {
        const fillColor = bar.fraction < 0.34 ? FILL_LOW : FILL_HIGH;
        this.patchMeterOverlay.rect(x, y, width * bar.fraction, height).fill({ color: fillColor });
      }
      this.patchMeterOverlay
        .rect(x, y, width, height)
        .stroke({ width: 1, color: 0x1a140b, alpha: 0.6 });
    }
  }

  private clearLayer(layer: Map<string, EntityNode>): void {
    for (const entity of layer.values()) {
      entity.node.destroy({ children: true });
    }
    layer.clear();
  }

  /**
   * Draws one tier layer from a pre-built y-sorted drawable list, keeping the
   * create-on-first-sight / mutate-in-place / destroy-on-disappear diff GC (design D10):
   * close-tier bees draw at their interpolated position (else the raw projection), pick the
   * gait sheet + heading frame, and hover-bob with an offset shadow in `full` mode; wide-tier
   * markers redraw their colony-tinted `Graphics`; every body is depth-scaled by `depthScale`.
   */
  private drawTier(
    layer: Map<string, EntityNode>,
    drawables: readonly WorldDrawable[],
    camera: Camera,
    alpha: number,
    animMode: AnimationMode,
    now: number,
  ): void {
    const seen = new Set<string>();
    const viewportHeight = this.app.screen.height || 1;
    for (const drawable of drawables) {
      const key = `${drawable.kind}:${drawable.id}`;
      seen.add(key);
      let entity = layer.get(key);
      if (entity === undefined) {
        entity = this.createNode(drawable);
        layer.set(key, entity);
      }
      // Interpolated screen position for close-tier bees (design D8), the raw projection
      // for the static kinds (patches / anchor / wide markers). A bee attached to a flower
      // (flower-attach-meter, `drawable.attachedTo`) renders at that stable world anchor
      // INSTEAD of the interpolated position — bypassing interpolation rather than fighting
      // it frame to frame, since the anchor is a fixed point, not a fresh sample to lerp
      // toward. Unset for every non-attached bee, which keeps the existing interpolated path.
      const { x: screenX, y: screenY } = beeDrawScreenPosition(
        drawable,
        camera,
        (id, t) => this.presentClock.positionAt(id, t),
        now,
      );
      // The node's scale is the depth cue TIMES the camera's own (`spriteCameraScale`):
      // a sprite is anchored to the world like every primitive already was, so a zoom
      // sweep no longer moves the ground while leaving the things standing on it fixed.
      const scale = depthScale(screenY, viewportHeight) * spriteCameraScale(camera.scale);
      // Draw y + per-node paint key (zIndex) from the shared placement seam: the hive keys on
      // its explicit top-edge `depth`, a keyless bee on this live/interpolated `screenY`
      // (hive-depth-order). `zIndex` is authoritative for paint (the layer is sortableChildren).
      const placement = resolveNodePlacement(drawable, screenY);
      entity.node.x = Math.round(screenX);
      entity.node.y = placement.y;
      entity.node.zIndex = placement.zIndex;
      // A corpse's own fade (design "Dead-bee corpse presentation") multiplies onto the
      // tier's whole-layer crossfade alpha — distinct concerns, never conflated. Unset
      // (`corpseAlpha` undefined) for every non-corpse drawable, so this is a no-op there.
      entity.node.alpha = alpha * (drawable.corpseAlpha ?? 1);
      if (entity.body instanceof Graphics) {
        this.drawMarker(entity.body, drawable);
        entity.body.scale.set(scale);
      } else {
        this.applySprite(entity, drawable, scale, animMode, now);
      }
    }
    for (const [key, entity] of layer) {
      if (!seen.has(key)) {
        entity.node.destroy({ children: true });
        layer.delete(key);
      }
    }
  }

  /** Creates a persistent display node for a first-sighted drawable: a directional `Sprite`
   * (anchored at its feet) for the textured kinds, a marker `Graphics` for the wide-tier
   * kinds. Entered into the y-sorted `world` layer; GC'd when the entity disappears. */
  private createNode(drawable: WorldDrawable): EntityNode {
    const node = new Container();
    let body: Sprite | Graphics;
    if (drawable.sheet !== undefined) {
      const sprite = new Sprite();
      sprite.anchor.set(0.5, 1); // feet at the node origin: the ground-contact / depth point
      body = sprite;
    } else {
      body = new Graphics();
    }
    node.addChild(body);
    this.world.addChild(node);
    return {
      kind: drawable.kind,
      node,
      body,
      shadow: null,
      flashOverlay: null,
      selectionSilhouette: null,
      appliedSheet: null,
      appliedFrame: -1,
      appliedTint: null,
    };
  }

  /** Selects + assigns a textured body's sprite frame (mutate-in-place — reassigned only on
   * a gait/heading change) and drives its depth scale, hover-bob, and shadow. Directional
   * sheets (bee gait sheet by heading, own-queen fixed frame 0) are frame-sliced; the flower
   * and hive textures are whole images. */
  private applySprite(
    entity: EntityNode,
    drawable: WorldDrawable,
    scale: number,
    animMode: AnimationMode,
    now: number,
  ): void {
    const body = entity.body as Sprite;
    const sheet = drawable.sheet as AssetKey;
    let frame = 0;
    let texture: Texture;
    // The bee/queen sheets draw from their palette-swapped variant (bee-body-palette): the
    // whole body carries the type/colony colour, so the recolour is baked into the texture
    // rather than applied as a per-frame tint (a Pixi `tint` is a flat multiply — it cannot
    // recolour one region and leave the band and wing alone).
    const tintKey = drawable.tint?.key ?? null;
    if (drawable.kind === 'bee') {
      frame = drawable.facing ?? 0;
      texture = this.frameTexture(sheet, frame, drawable.tint);
    } else if (drawable.kind === 'own-queen') {
      // Her derived heading where the caller supplied one, else frame 0 — the original
      // fixed-frame anchor behaviour (design D4), kept for a static anchor.
      frame = drawable.facing ?? 0;
      texture = this.frameTexture(sheet, frame, drawable.tint);
    } else {
      texture = this.assets.texture(sheet); // close-tier patch flower / hive: whole image
    }
    if (
      entity.appliedSheet !== sheet ||
      entity.appliedFrame !== frame ||
      entity.appliedTint !== tintKey
    ) {
      body.texture = texture;
      entity.appliedSheet = sheet;
      entity.appliedFrame = frame;
      entity.appliedTint = tintKey;
    }
    body.scale.set(scale);
    // A dead bee's body grey-tints in place (design "Dead-bee corpse presentation"); a live
    // bee with an active damage flash gets the flash's reduced-alpha SECONDARY body tint
    // (design E7/E8 — the outline below carries the flash, this is only the faint companion);
    // every other drawable stays untinted (`0xffffff`, Pixi's tint no-op). `flashPaintFor` is
    // the pure precedence resolver (D-086): it refuses to paint a `dead` drawable outright, so
    // the two arms below can never disagree with it. Set unconditionally each frame so a bee
    // never keeps a stale tint from an earlier state.
    const flash = flashPaintFor(drawable);
    if (drawable.dead) {
      body.tint = DEAD_BEE_TINT;
    } else if (flash !== null) {
      body.tint = flash.bodyTintColor;
    } else {
      body.tint = 0xffffff;
    }
    // The flash's PRIMARY carrier: an additive copy of the body's own texture, over the body — so
    // the WHOLE bee flashes red/white in its own silhouette. Independent of `cue`/gait below,
    // since a bee can flash while walking or flying.
    if (flash !== null) {
      const overlay = this.ensureFlashOverlay(entity);
      overlay.visible = true;
      overlay.texture = body.texture;
      overlay.tint = flash.overlayColor;
      overlay.alpha = flash.overlayAlpha;
      overlay.scale.set(body.scale.x, body.scale.y);
    } else if (entity.flashOverlay !== null) {
      entity.flashOverlay.visible = false;
    }

    // Selection is a same-size foreground copy. Damage flash is inserted above it and therefore
    // remains fully legible when both states are active.
    const selection = selectionPaintFor(drawable, now, animMode);
    if (selection !== null) {
      const silhouette = this.ensureSelectionSilhouette(entity);
      silhouette.visible = true;
      silhouette.texture = body.texture;
      silhouette.tint = selection.color;
      silhouette.alpha = selection.alpha;
      silhouette.scale.set(body.scale.x, body.scale.y);
    } else if (entity.selectionSilhouette !== null) {
      entity.selectionSilhouette.visible = false;
    }

    // **One `y`, computed by the branch below and assigned once at the end.** The flash overlay and
    // the selection silhouette are copies of the body's texture and must sit on the same offset, and
    // they were configured above — before this branch runs — so they cannot take it there. Assigning
    // `body.y` inside each arm and syncing them afterwards worked but left four places a future arm
    // could forget; a single assignment site cannot be forgotten by construction.
    let bodyY = 0;
    if (drawable.kind !== 'bee') {
      this.applyBodyY(entity, body, 0);
      return;
    }
    // The per-bee ground depth cue (design D2/D7): a flying bee in `full` mode hover-bobs above an
    // offset FILLED shadow ellipse; a flying bee in `reduced` drops both bob and shadow; a WALKING
    // bee sits on **that same filled ellipse**, directly beneath it and without the bob, in every
    // mode. That last clause **reverses implementation-review F14**, which had replaced the ellipse
    // with a stroked ground line to match the kit mock's `groundLine`: the line read as a scratch
    // under the bee at projector scale, and `GROUND_SINK` now shifts sprite and shadow *together*
    // so the flying hover gap is preserved by construction. The kit mock was updated to match
    // (`prototype/sketches/pixel-art-pass/close-bee-tiers.html`). The decision is the pure
    // `depthCueFor` seam (unit-tested); the Graphics paint stays GPU-real (D-086).
    const cue = depthCueFor(drawable.gait ?? 'walk', animMode);
    if (cue === 'hover-shadow') {
      const bob = this.presentClock.hoverBob(now, drawable.id);
      // Both the sprite and the shadow drop by `GROUND_SINK`, so the gap between them is exactly
      // `BASE_HOVER + bob` — the same jump a flying bee had before the sink existed.
      bodyY = (GROUND_SINK - BASE_HOVER - bob) * scale;
      this.drawShadowEllipse(entity, scale, SHADOW_OFFSET_X);
    } else if (cue === 'ground-shadow') {
      // Walking bees: the same filled ellipse, directly beneath (no x-offset — that shift is the
      // "floating above the ground" cue) and no bob, so the bee reads as standing in its shadow.
      bodyY = GROUND_SINK * scale;
      this.drawShadowEllipse(entity, scale, 0, shadowYFor('ground-shadow'));
    } else {
      // `none` (reduced/degraded flying): no bob, no ground cue at all.
      bodyY = GROUND_SINK * scale;
      if (entity.shadow !== null) {
        entity.shadow.visible = false;
      }
    }
    this.applyBodyY(entity, body, bodyY);
  }

  /**
   * Puts the body **and every copy of it** on the same vertical offset.
   *
   * The flash overlay and the selection silhouette are copies of the body's own texture, so they only
   * register with the bee if they share its `y`. They did not: both were configured earlier in
   * `applySprite` and left at their default `y = 0` while the body sat at `GROUND_SINK * scale`
   * (walking) or `(GROUND_SINK - BASE_HOVER - bob) * scale` (flying). At close-tier camera scales of
   * 3–5 that is 6–12 screen px, so the "whole bee flashes in its own silhouette" treatment drew a red
   * ghost *beside* the bee, and the blue selection silhouette sat asymmetrically off the bee it was
   * pointing at. Live and rounds mode draw through this same path, so it was never demo-only.
   *
   * Kept as the single place the offset is applied so a later gait arm cannot introduce a fourth
   * `body.y = …` that forgets the copies.
   */
  private applyBodyY(entity: EntityNode, body: Sprite, bodyY: number): void {
    body.y = bodyY;
    if (entity.flashOverlay !== null) {
      entity.flashOverlay.y = bodyY;
    }
    if (entity.selectionSilhouette !== null) {
      entity.selectionSilhouette.y = bodyY;
    }
  }

  /** Paints a bee's filled shadow ellipse at the sunk ground line, shifted `offsetX` px (pre-scale)
   * to the side. One place, so the flying and walking cues cannot drift into different shapes. */
  private drawShadowEllipse(
    entity: EntityNode,
    scale: number,
    offsetX: number,
    shadowY = shadowYFor('hover-shadow'),
  ): void {
    const shadow = this.ensureShadow(entity);
    shadow.visible = true;
    shadow.clear();
    shadow
      .ellipse(offsetX * scale, shadowY * scale, 4 * scale, 2 * scale)
      .fill({ color: 0x000000, alpha: 0.22 });
  }

  /** Lazily creates the bee's shadow `Graphics` beneath its body. */
  private ensureShadow(entity: EntityNode): Graphics {
    if (entity.shadow === null) {
      const shadow = new Graphics();
      entity.node.addChildAt(shadow, 0); // beneath the body sprite
      entity.shadow = shadow;
    }
    return entity.shadow;
  }

  /** Lazily creates the bee's damage-flash overlay: a copy of its own texture, additively blended
   * OVER the body, so the flash takes the bee's exact silhouette and can go brighter than the
   * sprite (a `tint` is a multiply and cannot lighten, which is why the near-white core phase
   * needs an additive pass rather than a tint). */
  private ensureFlashOverlay(entity: EntityNode): Sprite {
    if (entity.flashOverlay === null) {
      const overlay = new Sprite();
      overlay.anchor.set(0.5, 1); // same anchor as the body, so it registers exactly
      overlay.blendMode = 'add';
      entity.node.addChild(overlay); // over the body
      entity.flashOverlay = overlay;
    }
    return entity.flashOverlay;
  }

  /** Lazily creates the selected bee's same-size foreground overlay. */
  private ensureSelectionSilhouette(entity: EntityNode): Sprite {
    if (entity.selectionSilhouette === null) {
      const silhouette = new Sprite();
      silhouette.anchor.set(0.5, 1);
      silhouette.tint = SELECTION_OUTLINE_COLOR;
      const bodyIndex = entity.node.getChildIndex(entity.body);
      entity.node.addChildAt(silhouette, bodyIndex + 1); // body < selection < later flash overlay
      entity.selectionSilhouette = silhouette;
    }
    return entity.selectionSilhouette;
  }

  /** Redraws a wide-tier marker `Graphics` (design D6), tinted by its `--colony-N` token (or
   * a per-kind fallback for the un-colonied patch/ruin markers). Drawn at base size; the
   * caller applies the depth scale via `body.scale`. */
  private drawMarker(g: Graphics, drawable: WorldDrawable): void {
    g.clear();
    const color = resolveFillColor(
      drawable.color,
      drawable.color ? COLONY_COLOR_FALLBACK : PLACEHOLDER_COLOR[drawable.kind],
    );
    switch (drawable.marker) {
      case 'swarm':
        g.circle(0, 0, 6).fill(color); // colony-coloured dot swarm
        break;
      case 'hive-marker':
        g.rect(-6, -6, 12, 12).stroke({ width: 1.5, color }); // nested squares
        g.rect(-3, -3, 6, 6).stroke({ width: 1, color });
        break;
      case 'bloom':
        g.ellipse(0, 0, 5, 3).fill({ color, alpha: 0.85 });
        break;
      case 'ruin-marker':
        g.rect(-4, -4, 8, 8).fill({ color, alpha: 0.55 });
        break;
      default:
        g.circle(0, 0, 4).fill(color); // defensive: a missing marker draws a visible dot
    }
  }

  /** The cached frame sub-texture for a sheet column (design D1/D3): a square window into
   * the sheet at column `frame`, allocated once per (sheet, tint, frame). `tint` selects the
   * palette-swapped sheet variant (bee-body-palette); omitted, the raw loaded sheet is used.
   * `size` defaults to the 24px directional pitch — the comb sheet passes its own. */
  private frameTexture(key: AssetKey, frame: number, tint?: BeeTint, size = FRAME_SIZE): Texture {
    // `size` is deliberately not in the key: a sheet has exactly one frame pitch, so `key`
    // already determines it, and this string is rebuilt per bee per frame.
    const cacheKey = `${key}:${tint?.key ?? ''}:${frame}`;
    let texture = this.frameCache.get(cacheKey);
    if (texture === undefined) {
      const sheet = tint === undefined ? this.assets.texture(key) : this.variantSheet(key, tint);
      texture = new Texture({
        source: sheet.source,
        frame: new Rectangle(frame * size, 0, size, size),
      });
      this.frameCache.set(cacheKey, texture);
    }
    return texture;
  }

  /** The palette-swapped variant of `key` for `tint` (bee-body-palette), realised on first
   * use through {@link recolorSheet} and cached — so the CPU recolour is paid once per
   * (sheet, colour pair) for the whole match, never per bee and never per frame. */
  private variantSheet(key: AssetKey, tint: BeeTint): Texture {
    const cacheKey = `${key}:${tint.key}`;
    const cached = this.variantSheets.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }
    // A failed recolour falls back to the plain sheet and is NOT cached as a variant: that
    // texture is owned by `Assets`, and destroying it with this renderer would tear a shared
    // sheet out from under every other consumer.
    const variant = recolorSheet(this.assets.texture(key), tint);
    if (variant === null) {
      return this.assets.texture(key);
    }
    this.variantSheets.set(cacheKey, variant);
    return variant;
  }

  destroy(): void {
    this.loop.destroy();
    this.clearLayer(this.sprites);
    this.clearLayer(this.oldSprites);
    for (const sprite of this.scatterSprites.values()) {
      sprite.destroy();
    }
    this.scatterSprites.clear();
    // The comb pool's sprites are ours; their textures are frame windows released below.
    for (const sprite of this.hiveCellSprites) {
      sprite.destroy();
    }
    this.hiveCellSprites.length = 0;
    for (const texture of this.frameCache.values()) {
      texture.destroy(); // frame windows only — the shared sheet source is owned by Assets
    }
    this.frameCache.clear();
    for (const texture of this.variantSheets.values()) {
      texture.destroy(true); // this renderer OWNS its recoloured sources, unlike Assets' sheets
    }
    this.variantSheets.clear();
    for (const texture of this.groundGrainTextures.values()) texture.destroy(true);
    this.groundGrainTextures.clear();
    // The label textures are this renderer's own (design E8) — released here beside the frame
    // windows and the palette variants, the same single-ownership rule.
    this.stateLabels.invalidate();
    this.stateLabelSprites.length = 0;
    this.stateLabelLayer.destroy({ children: true });
    this.ring.destroy();
    this.playBoundary.destroy();
    this.beeBarOverlay.destroy();
    this.symbolOverlay.destroy();
    this.danceOverlay.destroy();
    if (this.app.renderer) {
      // Guarded on the same field Pixi itself uses (review-frame-renderer-leak F4 follow-up,
      // corrected after the implementation review): pixi.js's `Application.destroy()` is
      // unconditional — it calls `this.renderer.destroy(...)` even when `renderer` was never
      // assigned, which happens whenever `app.init()` rejects before `autoDetectRenderer`
      // settles (no WebGL context). A lifecycle flag set only after the *whole* `app.init()`
      // resolves is the wrong predicate: pixi assigns `this.app.renderer` as `init()`'s first
      // step and only then runs its plugin-init pass (ticker start, resize wiring) — a plugin
      // throwing after that point would leave a real renderer (and a running ticker) behind
      // while still failing `init()`. Checking `this.app.renderer` directly covers that window
      // exactly, and — because `Application.destroy()` sets `this.renderer = null` — makes this
      // `destroy()` idempotent for free (`client-rendering`'s own requirement: "`init`/`destroy`
      // MUST be idempotent"), which the flag version was not.
      this.app.destroy(true, { children: true });
    }
  }
}
